/**
 * Import CSV d'identifiants : Bitwarden, Chrome/Edge, Firefox, LastPass,
 * Dashlane, NordPass, Keeper, RoboForm, Zoho Vault, Myki / JumpCloud, Avira, McAfee True Key, KeePass (CSV), Buttercup, Padloc, mSecure, Codebook, Netwrix Password Secure, Encryptr, RememBear, Passpack, Blur (Abine), Meldium, LogMeOnce, PassKeep, SaferPass, Yoti, 1Password (CSV), Proton Pass, export generique.
 *
 * Purement local : le fichier choisi par l'utilisateur ne quitte jamais
 * l'appareil, il est parse puis chaque ligne est chiffree via
 * `VaultStore.createEntry` (`vault-store.ts`). Ce module ne fait QUE le
 * parsing texte -> objets ; aucune E/S, aucun reseau.
 */
import { base64ToBytes, parseTotpSeed, TOTP_DEFAULTS, type EntryCustomField, type EntrySecrets, type TotpSeed } from '@klavox/crypto-core';
import { normalizeUriMatch, type UriMatchRule, type VaultItemType } from '@klavox/vault-model';
import { checkboxValue, normalizeCustomFields } from './custom-fields.js';
import type { PasskeyCredential } from './passkey-credential.js';
import { normalizePasskeys } from './passkey-credential.js';
import { readPasswordRenewDays } from './password-expiry.js';
import type { PasswordHistoryEntry } from './password-history.js';
import { normalizeTags, parseTagList } from './entry-tags.js';
import { readIgnoredHealthAlerts, type IgnorableHealthAlert } from './health-exclusion.js';
import { resolveEntryReferences, type ReferenceIndex, type ReferenceTarget } from './keepass-references.js';
import { withSshKey, type SshKeyMaterial } from './ssh-key-item.js';
import { normalizeDocumentDate, normalizeExpiry, normalizeTypedFields, serverHost, typedFieldGroup, type TypedFields } from './typed-fields.js';

export interface ImportedEntry {
  readonly type: VaultItemType;
  readonly name: string;
  readonly username: string;
  readonly password: string;
  readonly uris: readonly string[];
  /** Regle de correspondance par adresse (Bitwarden `login.uris[].match`, `uri-match.ts`) ; absente = domaine. */
  readonly uriMatch?: Readonly<Record<string, UriMatchRule>>;
  readonly notes: string;
  /** Graine TOTP base32 normalisee ; vide si absente. */
  readonly totpSecret: string;
  readonly favorite: boolean;
  /** Champs types (carte/identite, `typed-fields.ts`), presents seulement pour ces types. */
  readonly fields?: TypedFields;
  /** Anciens mots de passe (plus recent d'abord), rattaches a l'historique chiffre de l'entree. */
  readonly passwordHistory?: readonly PasswordHistoryEntry[];
  /**
   * Instant (ms) ou le mot de passe courant a ete pose, quand l'export le date (Bitwarden `passwordRevisionDate`,
   * creation de l'element 1Password) : la Sante du coffre juge les fuites de sites et l'age du mot de passe sur cette
   * date, pas sur celle de l'import (`password-history.ts`).
   */
  readonly passwordChangedAt?: number;
  /** Echeance de changement du mot de passe (`AAAA-MM-JJ`, KeePass « Expires »), signalee par la Sante du coffre (`password-expiry.ts`). */
  readonly passwordExpiry?: string;
  /** Regle de renouvellement en jours, brute (export CXF de Klavox) : bornee par `readPasswordRenewDays` (`password-expiry.ts`). */
  readonly passwordRenewDays?: string;
  /** Champs personnalises de l'export (Bitwarden, KeePass, CXF, 1Password, Proton Pass, colonnes CSV non reconnues), masquage compris (`custom-fields.ts`). */
  readonly customFields?: readonly EntryCustomField[];
  /** Passkeys du compte (CXF), scellees avec l'entree ; cle privee comprise (`passkey-credential.ts`). */
  readonly passkeys?: readonly PasskeyCredential[];
  /** L'export marque l'entree « redemander le mot de passe maitre » (Bitwarden `reprompt: 1`, `master-password-reprompt.ts`). */
  readonly reprompt?: boolean;
  /** L'export classe l'entree aux archives (1Password `Archived`/`state: archived`, Bitwarden `archivedDate`, `entry-archive.ts`). */
  readonly archived?: boolean;
  /** L'entree est exclue de la Sante du coffre (etiquette CXF `health-excluded` d'un export Klavox, `health-exclusion.ts`). */
  readonly healthExcluded?: boolean;
  /** Alertes de la Sante du coffre ignorees une a une (etiquettes CXF `health-ignored-*` d'un export Klavox). */
  readonly healthIgnored?: readonly IgnorableHealthAlert[];
  /** Etiquettes de l'export (1Password, KeePass, CXF, colonne `tags` d'un CSV), rangees en etiquettes (`entry-tags.ts`) et non plus en notes. */
  readonly tags?: readonly string[];
  /**
   * Chemin du dossier d'origine (groupe KeePass, dossier Bitwarden, coffre
   * Proton Pass, colonne `folder`/`grouping` d'un CSV), de la racine vers la
   * feuille. L'UI recree l'arborescence a l'import ; vide = racine du coffre.
   */
  readonly folderPath?: readonly string[];
  /** Pieces jointes embarquees dans l'export (KDBX 4) ; chiffrees a l'import comme toute piece jointe. */
  readonly attachments?: readonly ImportedAttachment[];
  /** Cle SSH de l'export (Bitwarden, 1Password, Proton Pass), rangee en paire et non en notes (`ssh-key-item.ts`). */
  readonly sshKey?: SshKeyMaterial;
  /** Identifiant de l'element DANS l'export (1Password `uuid`) : sert seulement a resoudre les liens, jamais scelle. */
  readonly sourceId?: string;
  /** Elements lies, designes par leur `sourceId` (champ `reference` de 1Password), relies a l'import (`entry-links.ts`). */
  readonly linkedSourceIds?: readonly string[];
  /** References de champ KeePass (`{REF:...}`) laissees telles quelles faute de cible unique : comptees a l'import. */
  readonly unresolvedReferences?: number;
}

/** Piece jointe portee par un export concurrent, en clair tant qu'elle n'est pas chiffree par le coffre. */
export interface ImportedAttachment {
  readonly fileName: string;
  readonly bytes: Uint8Array;
}

/**
 * L'export choisi est chiffre par un mot de passe (KDBX, export Bitwarden
 * protege) : l'UI doit le demander puis relancer l'import. Le mot de passe
 * n'est ni conserve ni journalise.
 */
export class ImportPasswordRequiredError extends Error {
  override readonly name = 'ImportPasswordRequiredError';
  constructor(readonly format: string) {
    super(`fichier ${format} chiffre : mot de passe requis`);
  }
}

/** Mot de passe refuse par l'export (authentification du fichier en echec) — jamais un dechiffrement partiel. */
export class ImportPasswordInvalidError extends Error {
  override readonly name = 'ImportPasswordInvalidError';
  constructor(readonly format: string) {
    super(`mot de passe incorrect pour cet export ${format}`);
  }
}

/** Colonnes reconnues, par alias (en minuscules), du plus specifique au plus generique. */
const HEADER_ALIASES = {
  name: ['name', 'title'],
  username: ['login_username', 'username', 'user', 'login'],
  email: ['email'],
  password: ['login_password', 'password'],
  // `web` : export CSV d'Avast Passwords (`name,web,login,password`).
  uris: ['login_uri', 'url', 'uri', 'website', 'web'],
  notes: ['notes', 'note', 'extra', 'comment'],
  totp: ['login_totp', 'totp', 'otpauth', 'otpsecret', 'otp'],
  favorite: ['favorite', 'fav'],
  reprompt: ['reprompt'],
  archived: ['archived'],
  tags: ['tags', 'tag'],
  type: ['type'],
  folder: ['folder', 'grouping', 'vault'],
} as const;

/**
 * Colonnes de metadonnees des exports connus, sans valeur pour l'utilisateur :
 * toute AUTRE colonne non reconnue devient un champ personnalise (aucune perte
 * silencieuse, ex. `username2` Dashlane) ; la colonne `fields` Bitwarden est
 * decoupee en ses champs (`parseBitwardenCsvFields`).
 */
const IGNORED_COLUMNS = new Set([
  'category',
  'tags',
  'archived',
  'guid',
  'httprealm',
  'formactionorigin',
  'timecreated',
  'timelastused',
  'timepasswordchanged',
  'createtime',
  'modifytime',
  'login_fido2credentials',
]);

/**
 * Instant (ms) ou le mot de passe d'une ligne CSV a ete pose : Firefox le date
 * lui-meme (`timePasswordChanged`, millisecondes) ; Proton Pass ne date que
 * l'element (`modifyTime`, secondes), derniere modification = borne haute,
 * jamais `createTime`. `null` si rien n'est lisible : l'import vaut pose.
 */
function csvPasswordChangedAt(firefoxChanged: string, protonModified: string): number | null {
  if (/^[1-9]\d*$/u.test(firefoxChanged)) return Number(firefoxChanged);
  if (/^[1-9]\d*$/u.test(protonModified)) return Number(protonModified) * 1000;
  return null;
}

/** Colonne `fields` d'un export CSV Bitwarden : un champ personnalise par ligne `nom: valeur`. */
const BITWARDEN_FIELDS_COLUMN = 'fields';

/**
 * Libelle de colonne qui designe un secret (mot de passe, PIN, CVV, IBAN...) :
 * le CSV ne porte aucun drapeau de masquage, la valeur reste donc masquee a
 * l'ecran par prudence. Simple indication d'affichage, la valeur est scellee
 * de la meme facon. Lu sur le libelle en mots (`secretLabelWords`) : un export
 * camelCase (`pinCode`, `bankAccountNumber`, `securityCode`) ou un tableur
 * francais (« Cryptogramme », « Code de sécurité », « Clé privée ») dit le
 * meme secret qu'un `PIN` ou un `account_number`.
 */
const SECRET_COLUMN = /pass|secret|cvv|cvc|otp|iban/u;

/** Mots (ou suites de mots) d'un libelle de secret, cherches mot entier dans `secretLabelWords`. */
const SECRET_LABEL_WORDS =
  /(?:^| )(?:pin|nip|puk|pwd|pw|mdp|csc|cryptogramme|kennwort|wachtwoord|contrasena|senha|token|jeton|seed|mnemonic|account (?:no|nr|number)|numero de compte|security code|code (?:de )?securite|private key|cle privee|(?:api|access) key|cle d api|(?:recovery|backup) (?:codes?|key|phrase)|codes? de (?:secours|recuperation)|cle de recuperation|phrase de recuperation)(?= |$)/u;

/**
 * Libelle ramene a des mots minuscules separes d'une espace : accents
 * retires, coupe aux majuscules d'un camelCase (`pinCode`, `PINCode`), entre
 * lettres et chiffres (`PIN2`) et sur toute ponctuation (`account_number`).
 */
function secretLabelWords(label: string): string {
  return label
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/(?<=\p{Ll})(?=\p{Lu})|(?<=\p{Lu})(?=\p{Lu}\p{Ll})|(?<=\p{L})(?=\p{N})|(?<=\p{N})(?=\p{L})/gu, ' ')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
}

/** Vrai si le libelle d'un champ designe un secret, a masquer a l'ecran. */
export function isSecretLabel(label: string): boolean {
  return SECRET_COLUMN.test(label.toLowerCase()) || SECRET_LABEL_WORDS.test(secretLabelWords(label));
}

/** Champ personnalise tire d'une colonne CSV non reconnue (masque si son libelle designe un secret). */
export function csvColumnField(label: string, value: string): EntryCustomField {
  return { name: label, value, hidden: isSecretLabel(label) };
}

/**
 * Cellule d'une colonne libre (non reconnue) gardee en champ personnalise ;
 * `null` si elle est vide. La valeur est rognee, SAUF sous un libelle de
 * secret : ses espaces font partie du secret, comme ceux d'un mot de passe.
 */
export function csvFreeColumnField(label: string, cell: string): EntryCustomField | null {
  if (cell.trim().length === 0) return null;
  return csvColumnField(label, isSecretLabel(label) ? cell : cell.trim());
}

/**
 * Colonne `fields` du CSV Bitwarden : lignes `nom: valeur`, coupees au DERNIER
 * `: ` comme le fait Bitwarden a la reimportation. Le CSV ne dit pas quels
 * champs etaient masques : le libelle en decide (`isSecretLabel`). Une ligne
 * sans separateur garde sa valeur sous le nom par defaut, jamais perdue.
 */
export function parseBitwardenCsvFields(raw: string): EntryCustomField[] {
  return raw
    .split(/\r?\n/u)
    .filter((line) => line.trim().length > 0)
    .map((line) => {
      const separator = line.lastIndexOf(': ');
      if (separator === -1) return { name: '', value: line, hidden: false };
      return csvColumnField(line.slice(0, separator), line.slice(separator + 2));
    });
}

/** URL factice des notes securisees LastPass. */
const LASTPASS_SECURE_NOTE_URL = 'http://sn';

/** Decoupe une ligne CSV en respectant le guillemetage RFC 4180 (`""` = `"` litteral) ; `;` pour les exports a point-virgule. */
export function parseCsvLine(line: string, delimiter = ','): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const char = line[i]!;
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
      continue;
    }
    if (char === '"') {
      inQuotes = true;
    } else if (char === delimiter) {
      fields.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current);
  return fields;
}

/** Decoupe un texte CSV complet en lignes logiques, en respectant les retours a la ligne dans un champ guillemete. */
export function splitCsvRecords(text: string): string[] {
  const records: string[] = [];
  let current = '';
  let inQuotes = false;
  const normalized = text.replace(/^﻿/u, '').replace(/\r\n/gu, '\n');
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i]!;
    if (char === '"') inQuotes = !inQuotes;
    if (char === '\n' && !inQuotes) {
      records.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  if (current.length > 0) records.push(current);
  return records.filter((record) => record.trim().length > 0);
}

/**
 * Normalise une graine TOTP : base32 nu (espaces retires, majuscules) ou URI
 * `otpauth://totp/...?secret=...`. Une URI aux parametres non standard
 * (periode/chiffres/algorithme) est gardee ENTIERE comme graine :
 * `@klavox/crypto-core#totp` l'interprete. Une graine Steam Guard
 * `steam://<base32>` (Bitwarden) garde son prefixe. Seule une URI
 * inexploitable (HOTP, illisible, sans graine) part en `extraNote`, pour ne
 * rien perdre.
 */
export function normalizeTotpSecret(raw: string): { secret: string; extraNote: string } {
  const value = raw.trim();
  if (value.length === 0) return { secret: '', extraNote: '' };
  if (/^steam:\/\//iu.test(value)) return { secret: `steam://${value.slice('steam://'.length).replace(/[\s-]+/gu, '').toUpperCase()}`, extraNote: '' };
  if (!/^otpauth:\/\//iu.test(value)) return { secret: value.replace(/[\s-]+/gu, '').toUpperCase(), extraNote: '' };
  let seed: TotpSeed;
  try {
    seed = parseTotpSeed(value);
  } catch {
    return { secret: '', extraNote: `TOTP : ${value}` };
  }
  const standard =
    seed.periodSeconds === TOTP_DEFAULTS.periodSeconds &&
    seed.digits === TOTP_DEFAULTS.digits &&
    seed.algorithm === TOTP_DEFAULTS.algorithm;
  return { secret: standard ? seed.secret.replace(/[\s-]+/gu, '').toUpperCase() : value, extraNote: '' };
}

/**
 * Chemin de dossier a partir d'une colonne `folder`/`grouping`/`vault` :
 * LastPass et Bitwarden separent les niveaux par `/`, Dashlane n'a qu'un
 * niveau. Absent ou vide -> racine (propriete omise).
 */
export function folderPathOf(raw: string): { folderPath?: readonly string[] } {
  const path = raw
    .split(/[/\\]/u)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return path.length > 0 ? { folderPath: path } : {};
}

/** Assemble les notes d'une entree importee (notes d'origine puis complements `libelle : valeur`). */
export function joinNotes(parts: readonly string[]): string {
  return parts.filter((part) => part.trim().length > 0).join('\n');
}

/**
 * Parse un CSV d'identifiants en entrees normalisees.
 *
 * @throws {Error} pas de ligne d'en-tete, ou aucune colonne reconnue (mot de
 *   passe et identifiant/nom absents : rien d'exploitable)
 */
export function parseLoginCsv(text: string): ImportedEntry[] {
  const records = splitCsvRecords(text);
  if (records.length === 0) {
    throw new Error('fichier CSV vide');
  }
  // KeePass CSV (1.x) passe AVANT tout decoupage : `\"` y echappe un guillemet, que le decoupage RFC 4180 prendrait pour une fin de champ.
  const keePass1x = parseKeePassCsv1xRows(text);
  if (keePass1x !== null) return parseKeePass1xCells(keePass1x.columns, keePass1x.rows);
  // Netwrix passe EN PREMIER : separe par `;`, son en-tete n'est qu'une seule colonne pour les autres lecteurs.
  const netwrix = parseNetwrixCsv(records);
  if (netwrix !== null) return netwrix;
  const header = parseCsvLine(records[0]!).map((h) => h.trim());
  const normalizedHeader = header.map((h) => h.toLowerCase());
  // Zoho Vault avant Delinea : son ancien en-tete porte aussi `Secret Name`, seul indice de Delinea.
  const zohoVault = parseZohoVaultCsv(header, normalizedHeader, records.slice(1));
  if (zohoVault !== null) return zohoVault;
  const delinea = parseDelineaCsv(header, normalizedHeader, records.slice(1));
  if (delinea !== null) return delinea;
  const myki = parseMykiCsv(header, normalizedHeader, records.slice(1));
  if (myki !== null) return myki;
  const avira = parseAviraCsv(header, normalizedHeader, records.slice(1));
  if (avira !== null) return avira;
  const trueKey = parseTrueKeyCsv(header, normalizedHeader, records.slice(1));
  if (trueKey !== null) return trueKey;
  // KeePass CSV (1.x) re-enregistre par un tableur : guillemetage RFC 4180, memes colonnes.
  const keePass = isKeePass1xHeader(normalizedHeader)
    ? parseKeePass1xCells(normalizedHeader, records.slice(1).map((record) => parseCsvLine(record)))
    : parseKeePassCsv(header, normalizedHeader, records.slice(1).map((record) => parseCsvLine(record)));
  if (keePass !== null) return keePass;
  const buttercup = parseButtercupCsv(header, normalizedHeader, records.slice(1));
  if (buttercup !== null) return buttercup;
  const padloc = parsePadlocCsv(header, normalizedHeader, records.slice(1));
  if (padloc !== null) return padloc;
  const codebook = parseCodebookCsv(header, normalizedHeader, records.slice(1));
  if (codebook !== null) return codebook;
  const encryptr = parseEncryptrCsv(header, normalizedHeader, records.slice(1));
  if (encryptr !== null) return encryptr;
  const rememBear = parseRememBearCsv(header, normalizedHeader, records.slice(1));
  if (rememBear !== null) return rememBear;
  const passpack = parsePasspackCsv(header, normalizedHeader, records.slice(1));
  if (passpack !== null) return passpack;
  const blur = parseBlurCsv(header, normalizedHeader, records.slice(1));
  if (blur !== null) return blur;
  const meldium = parseNamedLoginCsv(MELDIUM_COLUMNS, header, normalizedHeader, records.slice(1));
  if (meldium !== null) return meldium;
  const yoti = parseNamedLoginCsv(YOTI_COLUMNS, header, normalizedHeader, records.slice(1));
  if (yoti !== null) return yoti;
  const logMeOnce = parseNamedLoginCsv(LOGMEONCE_COLUMNS, header, normalizedHeader, records.slice(1));
  if (logMeOnce !== null) return logMeOnce;
  const passKeep = parseNamedLoginCsv(PASSKEEP_COLUMNS, header, normalizedHeader, records.slice(1));
  if (passKeep !== null) return passKeep;
  const saferPass = parseNamedLoginCsv(SAFERPASS_COLUMNS, header, normalizedHeader, records.slice(1));
  if (saferPass !== null) return saferPass;
  // mSecure passe AVANT Keeper : sans en-tete lui aussi, il serait lu colonne par colonne comme une fiche Keeper.
  if (isMSecureCsv(records)) return parseMSecureCsv(records);
  if (isKeeperCsv(normalizedHeader, records)) return parseKeeperCsv(records);
  const roboForm = parseRoboFormCsv(normalizedHeader, records.slice(1));
  if (roboForm !== null) return roboForm;
  const nordPass = parseNordPassCsv(header, normalizedHeader, records.slice(1));
  if (nordPass !== null) return nordPass;
  const typed = parseTypedCsv(header, normalizedHeader, records.slice(1));
  if (typed !== null) return typed;
  const used = new Set<number>();
  const column = (field: keyof typeof HEADER_ALIASES): number => {
    for (const alias of HEADER_ALIASES[field]) {
      const index = normalizedHeader.indexOf(alias);
      if (index !== -1) {
        used.add(index);
        return index;
      }
    }
    return -1;
  };
  const columns = {
    name: column('name'),
    username: column('username'),
    email: column('email'),
    password: column('password'),
    uris: column('uris'),
    notes: column('notes'),
    totp: column('totp'),
    favorite: column('favorite'),
    reprompt: column('reprompt'),
    archived: column('archived'),
    tags: column('tags'),
    type: column('type'),
    folder: column('folder'),
  };
  if (columns.password === -1 && columns.username === -1 && columns.name === -1) {
    throw new Error('aucune colonne reconnue (nom, identifiant ou mot de passe attendu)');
  }
  const loginColumnsPresent = columns.password !== -1 || columns.username !== -1 || columns.uris !== -1;
  const firefoxChanged = normalizedHeader.indexOf('timepasswordchanged');
  const protonModified = normalizedHeader.indexOf('modifytime');

  const rows: ImportedEntry[] = [];
  for (const record of records.slice(1)) {
    const fields = parseCsvLine(record);
    const raw = (index: number): string => (index === -1 ? '' : (fields[index] ?? ''));
    const get = (index: number): string => raw(index).trim();
    let uri = get(columns.uris);
    const declaredType = get(columns.type).toLowerCase();
    // Bitwarden/Proton Pass : colonne `type` ; LastPass : URL factice `http://sn`.
    const isNote =
      declaredType === 'note' ||
      declaredType === 'securenote' ||
      uri.toLowerCase() === LASTPASS_SECURE_NOTE_URL ||
      (!loginColumnsPresent && columns.notes !== -1);
    if (uri.toLowerCase() === LASTPASS_SECURE_NOTE_URL) uri = '';
    const username = get(columns.username);
    const email = get(columns.email);
    const totp = normalizeTotpSecret(get(columns.totp));
    const customFields: EntryCustomField[] = [];
    if (email.length > 0 && username.length > 0 && email !== username) customFields.push(csvColumnField(header[columns.email]!, email));
    header.forEach((label, index) => {
      if (used.has(index) || IGNORED_COLUMNS.has(normalizedHeader[index]!)) return;
      const value = get(index);
      if (value.length === 0) return;
      if (normalizedHeader[index] === BITWARDEN_FIELDS_COLUMN) customFields.push(...parseBitwardenCsvFields(value));
      else customFields.push(csvFreeColumnField(label, raw(index))!);
    });
    const favorite = get(columns.favorite).toLowerCase();
    const tags = parseTagList(get(columns.tags));
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    const password = raw(columns.password);
    const passwordChangedAt = !isNote && password.length > 0 ? csvPasswordChangedAt(get(firefoxChanged), get(protonModified)) : null;
    rows.push({
      type: isNote ? 'note' : 'login',
      name: get(columns.name) || uri || username || email || 'Sans nom',
      username: username || email,
      password,
      uris: uri.length > 0 ? [uri] : [],
      notes: joinNotes([get(columns.notes), totp.extraNote]),
      totpSecret: totp.secret,
      favorite: favorite === '1' || favorite === 'true',
      ...(passwordChangedAt !== null ? { passwordChangedAt } : {}),
      // Bitwarden : `reprompt` = 1 quand l'entree redemande le mot de passe maitre ; repris tel quel.
      ...(get(columns.reprompt) === '1' ? { reprompt: true } : {}),
      // 1Password CSV : colonne `Archived` (`true`/`false`) ; l'entree reste aux archives chez nous.
      ...(['1', 'true'].includes(get(columns.archived).toLowerCase()) ? { archived: true } : {}),
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(tags.length > 0 ? { tags } : {}),
      ...folderPathOf(get(columns.folder)),
    });
  }
  return rows;
}

/** Pieces d'identite Dashlane (`ids.csv`, colonne `type`) -> champ d'identite. */
const DASHLANE_ID_FIELDS: Record<string, string> = {
  card: 'idCardNumber',
  passport: 'passportNumber',
  license: 'licenseNumber',
  social_security: 'socialSecurityNumber',
  tax_number: 'taxNumber',
};

/**
 * Exports Dashlane de cartes (`payments.csv`), d'identites (`personalInfo.csv`)
 * et de pieces (`ids.csv`), reconnus a leurs colonnes. Colonne -> champ type ;
 * toute autre colonne non vide devient un champ personnalise. `null` si le CSV
 * n'est pas de cette forme (import d'identifiants classique).
 */
function parseTypedCsv(header: string[], normalized: string[], records: string[]): ImportedEntry[] | null {
  const has = (name: string): boolean => normalized.includes(name);
  const kind = has('cc_number') ? 'payments' : has('number') && has('place_of_issue') ? 'ids' : has('first_name') || has('phone_number') || has('date_of_birth') ? 'personal' : null;
  if (kind === null) return null;
  const columns: Record<string, string> =
    kind === 'payments'
      ? { account_holder: 'cardholder', cc_number: 'number', code: 'cvv', issuing_bank: 'bank' }
      : kind === 'personal'
        ? { title: 'title', first_name: 'firstName', last_name: 'lastName', date_of_birth: 'birthDate', email: 'email', phone_number: 'phone', address: 'address', zip: 'postalCode', city: 'city', state: 'region', country: 'country' }
        : {};
  const consumed = new Set(['type', 'item_name', 'account_name', 'name', 'note', 'expiration_month', 'expiration_year', ...Object.keys(columns)]);
  return records.map((record) => {
    const values = parseCsvLine(record);
    const get = (name: string): string => (values[normalized.indexOf(name)] ?? '').trim();
    const rowType = get('type').toLowerCase();
    const raw: Record<string, string> = {};
    for (const [column, key] of Object.entries(columns)) raw[key] = get(column);
    let type: VaultItemType = kind === 'payments' ? (rowType === 'bank' ? 'note' : 'card') : 'identity';
    if (kind === 'payments') {
      const month = get('expiration_month');
      const year = get('expiration_year');
      if (month.length > 0 && year.length > 0) raw['expiry'] = `${month}/${year}`;
    } else if (kind === 'ids') {
      const key = DASHLANE_ID_FIELDS[rowType];
      if (key !== undefined) raw[key] = get('number');
      else type = 'note';
    }
    const customFields: EntryCustomField[] = [];
    header.forEach((label, index) => {
      const name = normalized[index]!;
      if (consumed.has(name) || (name === 'number' && type === 'identity') || IGNORED_COLUMNS.has(name)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    const group = typedFieldGroup(type);
    const fields = group === null ? {} : normalizeTypedFields(group, raw);
    const fullName = [fields['firstName'], fields['lastName']].filter(Boolean).join(' ');
    return {
      type,
      name: get('item_name') || get('account_name') || get('name') || fullName || fields['cardholder'] || 'Sans nom',
      username: '',
      password: '',
      uris: [],
      notes: get('note'),
      totpSecret: '',
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(group !== null ? { fields } : {}),
    };
  });
}

/**
 * Colonnes d'un export CSV NordPass consommees selon le type de la ligne ; les
 * autres colonnes non vides deviennent des champs personnalises.
 */
const NORDPASS_CONSUMED: Record<string, readonly string[]> = {
  login: ['url', 'additional_urls', 'username', 'password', 'email'],
  note: [],
  card: ['cardholdername', 'cardnumber', 'cvc', 'pin', 'expirydate'],
  identity: ['full_name', 'email', 'phone_number', 'address1', 'address2', 'zipcode', 'city', 'state', 'country'],
};

/** Type de ligne NordPass -> type d'entree ; `folder` = definition de dossier, pas une entree. */
const NORDPASS_TYPES: Record<string, VaultItemType> = {
  password: 'login',
  note: 'note',
  credit_card: 'card',
  identity: 'identity',
};

/**
 * Colonne `additional_urls` : tableau JSON d'adresses (forme de l'export), ou a
 * defaut une adresse par ligne. Une valeur illisible reste une adresse, jamais perdue.
 */
function nordPassUrls(raw: string): string[] {
  if (raw.length === 0) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter((url): url is string => typeof url === 'string').map((url) => url.trim());
  } catch {
    // pas du JSON : une adresse par ligne
  }
  return raw.split(/\r?\n/u).map((url) => url.trim());
}

/**
 * Colonne `custom_fields` : tableau JSON `{ label, type, value }`, `type: hidden`
 * = champ masque. Illisible, la valeur entiere est gardee sous le nom de la colonne.
 */
function nordPassCustomFields(raw: string): EntryCustomField[] {
  if (raw.length === 0) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .filter((field): field is Record<string, unknown> => typeof field === 'object' && field !== null)
        .map((field) => ({
          name: typeof field['label'] === 'string' ? field['label'] : '',
          value: typeof field['value'] === 'string' ? field['value'] : String(field['value'] ?? ''),
          hidden: field['type'] === 'hidden',
        }))
        .filter((field) => field.name.length > 0 || field.value.length > 0);
    }
  } catch {
    // illisible : garde tel quel ci-dessous
  }
  return [csvColumnField('custom_fields', raw)];
}

/**
 * Export CSV NordPass (une seule feuille pour tous les types, colonne `type` :
 * `password`, `note`, `credit_card`, `identity`, `folder`), reconnu a ses
 * colonnes `cardholdername` + `full_name`. Sans lui, la colonne `phone_number`
 * le faisait prendre pour un `personalInfo.csv` Dashlane : chaque identifiant
 * arrivait en identite vide, son mot de passe en champ personnalise. `null`
 * si le CSV n'est pas de cette forme.
 */
function parseNordPassCsv(header: string[], normalized: string[], records: string[]): ImportedEntry[] | null {
  if (!(normalized.includes('cardholdername') && normalized.includes('full_name') && normalized.includes('type'))) return null;
  const rows: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const cell = (name: string): string => {
      const index = normalized.indexOf(name);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (name: string): string => cell(name).trim();
    const rowType = get('type').toLowerCase();
    if (rowType === 'folder') continue;
    // Type inconnu (format a venir) : un identifiant s'il en porte les colonnes, sinon une note.
    const type: VaultItemType = NORDPASS_TYPES[rowType] ?? (get('password') || get('username') || get('url') ? 'login' : 'note');
    const consumed = new Set(['name', 'note', 'folder', 'type', 'custom_fields', ...(NORDPASS_CONSUMED[type] ?? [])]);
    const raw: Record<string, string> = {};
    if (type === 'card') {
      Object.assign(raw, { cardholder: get('cardholdername'), number: get('cardnumber'), cvv: get('cvc'), pin: get('pin'), expiry: get('expirydate') });
    } else if (type === 'identity') {
      const [firstName = '', ...lastName] = get('full_name').split(/\s+/u).filter(Boolean);
      Object.assign(raw, {
        firstName,
        lastName: lastName.join(' '),
        email: get('email'),
        phone: get('phone_number'),
        address: [get('address1'), get('address2')].filter(Boolean).join(', '),
        postalCode: get('zipcode'),
        city: get('city'),
        region: get('state'),
        country: get('country'),
      });
    }
    const customFields: EntryCustomField[] = [];
    header.forEach((label, index) => {
      const name = normalized[index]!;
      if (consumed.has(name) || IGNORED_COLUMNS.has(name)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    customFields.push(...nordPassCustomFields(get('custom_fields')));
    const group = typedFieldGroup(type);
    const fields = group === null ? {} : normalizeTypedFields(group, raw);
    const username = type === 'login' ? get('username') || get('email') : '';
    if (type === 'login' && get('username').length > 0 && get('email').length > 0 && get('email') !== get('username')) {
      customFields.unshift(csvColumnField('email', get('email')));
    }
    const uris = type === 'login' ? [get('url'), ...nordPassUrls(get('additional_urls'))].filter((url) => url.length > 0) : [];
    rows.push({
      type,
      name: get('name') || uris[0] || username || fields['cardholder'] || [fields['firstName'], fields['lastName']].filter(Boolean).join(' ') || 'Sans nom',
      username,
      // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
      password: type === 'login' ? cell('password') : '',
      uris: [...new Set(uris)],
      notes: get('note'),
      totpSecret: '',
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(group !== null ? { fields } : {}),
      ...folderPathOf(get('folder')),
    });
  }
  return rows;
}

/** Colonnes d'un export CSV Keeper (sans ligne d'en-tete), dans l'ordre du fichier. */
const KEEPER_CSV_COLUMNS = { folder: 0, title: 1, login: 2, password: 3, url: 4, notes: 5, sharedFolder: 6, firstCustom: 7 } as const;

/** Libelles d'en-tete reconnus : une premiere ligne qui en porte un est un en-tete, pas une fiche Keeper. */
const KNOWN_HEADER_LABELS = new Set<string>(Object.values(HEADER_ALIASES).flat());

/**
 * Export CSV Keeper : aucune ligne d'en-tete, au moins six colonnes par ligne
 * (dossier, titre, identifiant, mot de passe, adresse, notes, dossier partage,
 * puis paires nom / valeur de champs personnalises). Sans lui, la premiere
 * fiche etait lue comme un en-tete et le fichier refuse (« aucune colonne
 * reconnue »). Une premiere ligne qui porte un libelle connu reste un en-tete.
 */
function isKeeperCsv(normalizedHeader: readonly string[], records: readonly string[]): boolean {
  if (normalizedHeader.some((label) => KNOWN_HEADER_LABELS.has(label))) return false;
  return records.every((record) => parseCsvLine(record).length > KEEPER_CSV_COLUMNS.notes);
}

/** Champs personnalises Keeper qui portent la graine TOTP (`TFC:Keeper` ancien format, `$oneTimeCode` des fiches typees). */
const KEEPER_TOTP_FIELD = /^(tfc:keeper|\$onetimecode(:.*)?)$/iu;

/** Types de champs Keeper secrets par nature, masques quel que soit leur libelle. */
const KEEPER_SECRET_TYPES = new Set(['password', 'secret', 'pincode', 'onetimecode', 'securityquestion']);

/** Type de champ Keeper d'une case a cocher (`$checkbox:libelle`), rangee en case (`custom-fields.ts`). */
const KEEPER_CHECKBOX_TYPE = 'checkbox';

/**
 * Champs personnalises d'une fiche Keeper (paires du CSV, objet
 * `custom_fields` du JSON). Le nom `$type:libelle` d'une fiche typee devient
 * son libelle (`$type` seul : le type, sans `$`), une `$checkbox` une case a
 * cocher ; la premiere graine TOTP part au code a usage unique de l'entree,
 * une graine suivante reste un champ masque (aucune perte).
 */
export function keeperCustomFields(pairs: readonly (readonly [string, string])[]): {
  totp: { secret: string; extraNote: string };
  customFields: EntryCustomField[];
} {
  let totp = { secret: '', extraNote: '' };
  const customFields: EntryCustomField[] = [];
  for (const [rawName, rawValue] of pairs) {
    const name = rawName.trim();
    const value = rawValue.trim();
    if (value.length === 0) continue;
    if (KEEPER_TOTP_FIELD.test(name) && totp.secret.length === 0 && totp.extraNote.length === 0) {
      totp = normalizeTotpSecret(value);
      continue;
    }
    const typed = /^\$([a-z0-9]+)(?::(.*))?$/iu.exec(name);
    const label = typed === null ? name : (typed[2] ?? '').trim() || typed[1]!;
    // Case a cocher Keeper (`$checkbox`, valeur `true` / `false`) : une case, decochee comprise, jamais un texte « true ».
    if (typed !== null && typed[1]!.toLowerCase() === KEEPER_CHECKBOX_TYPE) {
      customFields.push({ name: label, value: checkboxValue(value), kind: 'boolean' });
      continue;
    }
    const secretType = typed !== null && KEEPER_SECRET_TYPES.has(typed[1]!.toLowerCase());
    customFields.push({ name: label, value, hidden: secretType || KEEPER_TOTP_FIELD.test(name) || isSecretLabel(label) });
  }
  return { totp, customFields };
}

/**
 * Chemin Keeper : le dossier partage (s'il y en a un) puis le dossier, chacun
 * aux niveaux separes par `\`. Un dossier deja prefixe par son dossier
 * partage n'est pas double.
 */
export function keeperFolderPath(folder: string, sharedFolder: string): { folderPath?: readonly string[] } {
  const own = folderPathOf(folder).folderPath ?? [];
  const shared = folderPathOf(sharedFolder).folderPath ?? [];
  const prefixed = shared.length > 0 && shared.every((part, index) => own[index] === part);
  const path = prefixed ? own : [...shared, ...own];
  return path.length > 0 ? { folderPath: path } : {};
}

/** Parse un export CSV Keeper ; chaque ligne, la premiere comprise, est une fiche. */
function parseKeeperCsv(records: readonly string[]): ImportedEntry[] {
  return records.map((record) => {
    const values = parseCsvLine(record);
    const get = (index: number): string => (values[index] ?? '').trim();
    const pairs: [string, string][] = [];
    for (let index = KEEPER_CSV_COLUMNS.firstCustom; index < values.length; index += 2) pairs.push([get(index), get(index + 1)]);
    const { totp, customFields } = keeperCustomFields(pairs);
    const username = get(KEEPER_CSV_COLUMNS.login);
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    const password = values[KEEPER_CSV_COLUMNS.password] ?? '';
    const url = get(KEEPER_CSV_COLUMNS.url);
    // Keeper n'a pas de colonne de type : une fiche sans identifiant, mot de passe, adresse ni code est une note.
    const isNote = username.length === 0 && password.length === 0 && url.length === 0 && totp.secret.length === 0;
    return {
      type: isNote ? 'note' : 'login',
      name: get(KEEPER_CSV_COLUMNS.title) || url || username || 'Sans nom',
      username,
      password,
      uris: url.length > 0 ? [url] : [],
      notes: joinNotes([get(KEEPER_CSV_COLUMNS.notes), totp.extraNote]),
      totpSecret: totp.secret,
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...keeperFolderPath(get(KEEPER_CSV_COLUMNS.folder), get(KEEPER_CSV_COLUMNS.sharedFolder)),
    };
  });
}

/** Nom d'un champ RoboForm sans libelle. */
const ROBOFORM_NO_NAME = '-no-name-';

/** Types de champs RoboForm (`RfFieldsV2`) qui portent un secret, masques quel que soit leur libelle. */
const ROBOFORM_SECRET_TYPES = new Set(['pwd', 'password']);

/** Champ RoboForm qui porte la graine TOTP (libelle `TOTP`, `TOTP KEY$`...). */
const ROBOFORM_TOTP_FIELD = /^totp/iu;

/** Champ RoboForm lu d'une cellule `RfFieldsV2` (`nom,,,type,valeur`) ou `Rf_fields` (`nom:...:valeur`). */
interface RoboFormField {
  readonly name: string;
  readonly value: string;
  readonly secret: boolean;
}

/**
 * Une cellule de champ RoboForm. `RfFieldsV2` : `nom,id,?,type,valeur`, la
 * valeur gardant ses virgules ; `Rf_fields` (ancien export) : `nom:id:valeur`,
 * ou `nom:valeur:rck:...` pour une case a cocher. Une cellule illisible est
 * gardee entiere sous un nom vide, jamais perdue.
 */
export function parseRoboFormField(cell: string, legacy: boolean): RoboFormField {
  const parts = cell.split(legacy ? ':' : ',');
  if (parts.length < (legacy ? 3 : 5)) return { name: '', value: cell.trim(), secret: false };
  const rawName = parts[0]!.trim();
  const name = rawName === ROBOFORM_NO_NAME ? '' : rawName;
  const secret = !legacy && ROBOFORM_SECRET_TYPES.has(parts[3]!.trim().toLowerCase());
  // Un secret n'est jamais rogne : ses espaces en font partie (`Password$` double le mot de passe brut).
  const kept = (value: string): string => (secret || name === 'Password$' ? value : value.trim());
  if (legacy) {
    const value = parts.length === 4 && parts[2] === 'rck' ? parts[1]! : parts.slice(2).join(':');
    return { name, value: kept(value), secret };
  }
  return { name, value: kept(parts.slice(4).join(',')), secret };
}

/** Meme site (origine) pour deux adresses ; une adresse illisible n'est jamais tenue pour identique. */
function sameOrigin(a: string, b: string): boolean {
  try {
    return new URL(a).origin === new URL(b).origin;
  } catch {
    return a === b;
  }
}

/**
 * Export CSV RoboForm (`Name,Url,MatchUrl,Login,Pwd,Note,Folder,RfFieldsV2`),
 * reconnu a sa colonne `Pwd` accompagnee de `RfFieldsV2`, `Rf_fields` ou
 * `MatchUrl`. Sans lui, `Pwd` n'etait pas un alias de mot de passe : le mot de
 * passe arrivait EN CLAIR dans un champ personnalise non masque, l'entree sans
 * mot de passe, et les champs RoboForm au-dela de la premiere cellule etaient
 * perdus. Chaque cellule apres `RfFieldsV2` est un champ (RoboForm ne les met
 * pas sous en-tete). `User ID$` / `Password$` doublant l'identifiant ou le mot
 * de passe sont ecartes ; differents, ils restent des champs. Une fiche sans
 * identifiant, mot de passe ni adresse est une note (Safenote). `null` si le
 * CSV n'est pas de cette forme.
 */
function parseRoboFormCsv(normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  const fieldsColumn = normalized.indexOf('rffieldsv2');
  const legacyColumn = normalized.indexOf('rf_fields');
  if (!(normalized.includes('pwd') && (fieldsColumn !== -1 || legacyColumn !== -1 || normalized.includes('matchurl')))) return null;
  const firstField = fieldsColumn !== -1 ? fieldsColumn : legacyColumn;
  return records.map((record) => {
    const values = parseCsvLine(record);
    const raw = (name: string): string => {
      const index = normalized.indexOf(name);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (name: string): string => raw(name).trim();
    let username = get('login');
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    let password = raw('pwd');
    let totp = { secret: '', extraNote: '' };
    const customFields: EntryCustomField[] = [];
    const cells = firstField === -1 ? [] : values.slice(firstField).filter((cell) => cell.trim().length > 0);
    for (const cell of cells) {
      const field = parseRoboFormField(cell, firstField === legacyColumn);
      if (field.value.trim().length === 0) continue;
      if (field.name === 'User ID$' && (username.length === 0 || field.value === username)) {
        username ||= field.value;
        continue;
      }
      if (field.name === 'Password$' && (password.length === 0 || field.value === password)) {
        password ||= field.value;
        continue;
      }
      const label = field.name.replace(/\$$/u, '');
      if (ROBOFORM_TOTP_FIELD.test(label) && totp.secret.length === 0 && totp.extraNote.length === 0) {
        totp = normalizeTotpSecret(field.value);
        continue;
      }
      customFields.push({ name: label, value: field.value, hidden: field.secret || field.name === 'Password$' || isSecretLabel(label) });
    }
    const url = get('url');
    const matchUrl = get('matchurl');
    const uris = [url, ...(matchUrl.length > 0 && !sameOrigin(matchUrl, url) ? [matchUrl] : [])].filter((uri) => uri.length > 0);
    const isNote = username.length === 0 && password.length === 0 && uris.length === 0 && totp.secret.length === 0;
    return {
      type: isNote ? 'note' : 'login',
      name: get('name') || url || username || 'Sans nom',
      username,
      password,
      uris,
      notes: joinNotes([get('note'), totp.extraNote]),
      totpSecret: totp.secret,
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...folderPathOf(get('folder')),
    };
  });
}

/** Cles `SecretData` Zoho Vault qui designent l'identifiant de connexion. */
const ZOHO_USERNAME_KEY = /^(user ?name|user ?id|login( ?name| ?id)?|e-?mail)$/iu;

/** Cles `SecretData` Zoho Vault qui designent le mot de passe de connexion. */
const ZOHO_PASSWORD_KEY = /^(password|passwd|pass)$/iu;

/** Colonnes Zoho Vault consommees par le lecteur ; `Classification` (personnel/entreprise) est une metadonnee. */
const ZOHO_CONSUMED = new Set([
  'password name', 'secret name', 'password url', 'secret url', 'description', 'notes', 'secretdata',
  'customdata', 'tags', 'classification', 'totp', 'login_totp', 'favorite', 'chambername', 'folder name',
]);

/**
 * Cellule `SecretData` / `CustomData` Zoho Vault : une ligne `cle:valeur` par
 * champ, coupee au PREMIER `:` (une adresse garde les siens). `SecretType`
 * (modele Zoho) est ecarte ; une ligne sans `:` est gardee entiere sous un nom
 * vide, jamais perdue.
 */
export function parseZohoVaultData(cell: string): { name: string; value: string }[] {
  const pairs: { name: string; value: string }[] = [];
  for (const line of cell.split(/\r?\n/u)) {
    if (line.trim().length === 0) continue;
    const separator = line.indexOf(':');
    if (separator === -1) {
      pairs.push({ name: '', value: line.trim() });
      continue;
    }
    const name = line.slice(0, separator).trim();
    const value = line.slice(separator + 1).trim();
    if (value.length === 0 || name.toLowerCase() === 'secrettype') continue;
    pairs.push({ name, value });
  }
  return pairs;
}

/**
 * Export CSV Zoho Vault (`Password Name,Description,Password URL,SecretData,
 * Notes,CustomData,Tags,Classification,TOTP,Favorite,ChamberName`), reconnu a
 * sa colonne `Password Name` (ou `Secret Name`) accompagnee de `SecretData` ou
 * `ChamberName`. Sans lui, aucune colonne n'etait reconnue et le fichier etait
 * REFUSE. L'identifiant et le mot de passe vivent dans `SecretData` (`User
 * Name:...`, `Password:...`) : le premier de chaque est repris a l'entree, les
 * autres cles (et celles de `CustomData`) deviennent des champs personnalises,
 * masques si leur libelle designe un secret. Une fiche sans identifiant, mot de
 * passe, adresse ni TOTP est une note. `null` si le CSV n'est pas de cette forme.
 */
function parseZohoVaultCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  const hasName = normalized.includes('password name') || normalized.includes('secret name');
  if (!(hasName && (normalized.includes('secretdata') || normalized.includes('chambername')))) return null;
  const rows: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const get = (name: string): string => {
      const index = normalized.indexOf(name);
      return index === -1 ? '' : (values[index] ?? '').trim();
    };
    let username = '';
    let password = '';
    const customFields: EntryCustomField[] = [];
    for (const pair of [...parseZohoVaultData(get('secretdata')), ...parseZohoVaultData(get('customdata'))]) {
      if (username.length === 0 && ZOHO_USERNAME_KEY.test(pair.name)) {
        username = pair.value;
        continue;
      }
      if (password.length === 0 && ZOHO_PASSWORD_KEY.test(pair.name)) {
        password = pair.value;
        continue;
      }
      customFields.push(csvColumnField(pair.name, pair.value));
    }
    header.forEach((label, index) => {
      if (ZOHO_CONSUMED.has(normalized[index]!) || IGNORED_COLUMNS.has(normalized[index]!)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    const url = get('password url') || get('secret url');
    const totp = normalizeTotpSecret(get('totp') || get('login_totp'));
    const name = get('password name') || get('secret name');
    const notes = joinNotes([get('description'), get('notes'), totp.extraNote]);
    const tags = parseTagList(get('tags'));
    const isNote = username.length === 0 && password.length === 0 && url.length === 0 && totp.secret.length === 0;
    if (isNote && name.length === 0 && notes.length === 0 && customFields.length === 0) continue;
    rows.push({
      type: isNote ? 'note' : 'login',
      name: name || url || username || 'Sans nom',
      username,
      password,
      uris: url.length > 0 ? [url] : [],
      notes,
      totpSecret: totp.secret,
      favorite: ['1', 'true'].includes(get('favorite').toLowerCase()),
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(tags.length > 0 ? { tags } : {}),
      ...folderPathOf(get('chambername') || get('folder name')),
    });
  }
  return rows;
}

/** Colonnes Myki consommees (en minuscules) ; toute autre colonne non vide devient un champ personnalise. */
const MYKI_CONSUMED = new Set([
  'nickname', 'additionalinfo', 'tags', 'url', 'username', 'password', 'twofasecret', 'authtoken',
  'cardnumber', 'cardname', 'exp_month', 'exp_year', 'cvv',
  'title', 'firstname', 'middlename', 'lastname', 'email', 'number', 'firstaddressline', 'secondaddressline', 'city', 'country', 'zipcode',
  'idtype', 'idnumber', 'idname', 'idexpirationdate', 'idcountry', 'content',
]);

/** Type de piece Myki (`idType`) -> champ de numero et champ d'expiration de l'identite. */
const MYKI_ID_FIELDS: readonly { readonly type: RegExp; readonly number: string; readonly expiry: string }[] = [
  { type: /passport|passeport/iu, number: 'passportNumber', expiry: 'passportExpiry' },
  { type: /driver|licen[cs]e|permis/iu, number: 'licenseNumber', expiry: 'licenseExpiry' },
  { type: /social|security|ssn/iu, number: 'socialSecurityNumber', expiry: '' },
  { type: /id(entity)?[\s_-]*card|identit/iu, number: 'idCardNumber', expiry: 'idCardExpiry' },
];

/**
 * Exports CSV de Myki (aujourd'hui JumpCloud Password Manager) : un fichier par
 * categorie, reconnu a sa colonne `nickname` accompagnee de `additionalInfo` ou
 * `status`. La categorie de chaque fichier se lit a ses colonnes : `url`
 * (comptes), `authToken` (jetons 2FA seuls), `cardNumber` (cartes),
 * `firstName` (identites), `idType` (pieces d'identite), `content` (notes).
 * Sans lui, seuls les comptes passaient — sans nom ni code TOTP — et les cinq
 * autres fichiers etaient REFUSES. Le mot de passe et le contenu d'une note ne
 * sont jamais rognes. `null` si le CSV n'est pas de cette forme.
 */
function parseMykiCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  const has = (name: string): boolean => normalized.includes(name);
  if (!(has('nickname') && (has('additionalinfo') || has('status')))) return null;
  const kind = has('url')
    ? 'account'
    : has('authtoken')
      ? 'twofa'
      : has('cardnumber')
        ? 'card'
        : has('firstname')
          ? 'identity'
          : has('idtype')
            ? 'id'
            : has('content')
              ? 'note'
              : null;
  if (kind === null) return null;
  const rows: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const raw = (name: string): string => {
      const index = normalized.indexOf(name);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (name: string): string => raw(name).trim();
    const customFields: EntryCustomField[] = [];
    const typed: Record<string, string> = {};
    let type: VaultItemType = 'login';
    let totpRaw = '';
    let notes = get('additionalinfo');
    if (kind === 'account') {
      totpRaw = get('twofasecret');
    } else if (kind === 'twofa') {
      totpRaw = get('authtoken') || get('twofasecret');
    } else if (kind === 'card') {
      type = 'card';
      typed['cardholder'] = get('cardname');
      typed['number'] = get('cardnumber');
      typed['cvv'] = get('cvv');
      const month = get('exp_month');
      const year = get('exp_year');
      if (month.length > 0 && year.length > 0) typed['expiry'] = `${month.padStart(2, '0')}/${year}`;
      else if (month.length > 0 || year.length > 0) customFields.push({ name: 'Expiration', value: month || year, hidden: false });
    } else if (kind === 'identity') {
      type = 'identity';
      typed['title'] = get('title');
      typed['firstName'] = [get('firstname'), get('middlename')].filter(Boolean).join(' ');
      typed['lastName'] = get('lastname');
      typed['email'] = get('email');
      typed['phone'] = get('number');
      typed['address'] = [get('firstaddressline'), get('secondaddressline')].filter(Boolean).join(', ');
      typed['city'] = get('city');
      typed['country'] = get('country');
      typed['postalCode'] = get('zipcode');
    } else if (kind === 'id') {
      type = 'identity';
      const holder = get('idname').split(/\s+/u).filter(Boolean);
      if (holder.length > 1) typed['lastName'] = holder.pop()!;
      typed['firstName'] = holder.join(' ');
      typed['country'] = get('idcountry');
      const idType = get('idtype');
      const number = get('idnumber');
      const target = MYKI_ID_FIELDS.find((candidate) => candidate.type.test(idType));
      if (target !== undefined) {
        typed[target.number] = number;
        if (target.expiry.length > 0) typed[target.expiry] = get('idexpirationdate');
        else if (get('idexpirationdate').length > 0) customFields.push(csvColumnField('idExpirationDate', get('idexpirationdate')));
      } else {
        // Piece d'un type que l'identite ne connait pas : son numero reste un champ, masque comme tout numero de piece.
        if (number.length > 0) customFields.push({ name: idType || 'idNumber', value: number, hidden: true });
        if (get('idexpirationdate').length > 0) customFields.push(csvColumnField('idExpirationDate', get('idexpirationdate')));
      }
    } else {
      type = 'note';
      notes = joinNotes([raw('content'), notes]);
    }
    header.forEach((label, index) => {
      if (MYKI_CONSUMED.has(normalized[index]!) || IGNORED_COLUMNS.has(normalized[index]!)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    const totp = normalizeTotpSecret(totpRaw);
    const group = typedFieldGroup(type);
    const fields = group === null ? {} : normalizeTypedFields(group, typed);
    const url = kind === 'account' ? get('url') : '';
    const username = kind === 'account' ? get('username') : '';
    const password = kind === 'account' ? raw('password') : '';
    const fullName = [fields['firstName'], fields['lastName']].filter(Boolean).join(' ');
    const name = get('nickname') || url || username || fullName || fields['cardholder'] || 'Sans nom';
    const tags = parseTagList(get('tags'));
    rows.push({
      type,
      name,
      username,
      password,
      uris: url.length > 0 ? [url] : [],
      notes: joinNotes([notes, totp.extraNote]),
      totpSecret: totp.secret,
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(group !== null && Object.keys(fields).length > 0 ? { fields } : {}),
      ...(tags.length > 0 ? { tags } : {}),
    });
  }
  return rows;
}

/** Colonnes Avira consommees (en minuscules) ; toute autre colonne non vide devient un champ personnalise. */
const AVIRA_CONSUMED = new Set(['name', 'website', 'username', 'password', 'secondary_username']);

/**
 * Export CSV d'Avira Password Manager (`name,website,username,password,secondary_username`),
 * reconnu a sa colonne `secondary_username` accompagnee de `website`. Sans lui,
 * le lecteur generique laissait SANS identifiant tout compte dont Avira ne
 * connait que l'identifiant secondaire (souvent l'e-mail) — le remplissage ne
 * savait plus quoi saisir —, rognait le mot de passe et nommait l'entree d'apres
 * l'URL entiere. Ici : l'identifiant secondaire devient l'identifiant quand le
 * principal est vide, sinon il reste un champ (jamais perdu) ; le mot de passe
 * n'est jamais rogne ; une entree sans nom prend l'hote du site. `null` si le
 * CSV n'est pas de cette forme.
 */
function parseAviraCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  if (!(normalized.includes('secondary_username') && normalized.includes('website'))) return null;
  const rows: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const raw = (name: string): string => {
      const index = normalized.indexOf(name);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (name: string): string => raw(name).trim();
    const website = get('website');
    const password = raw('password');
    const secondary = get('secondary_username');
    let username = get('username');
    const customFields: EntryCustomField[] = [];
    if (username.length === 0) username = secondary;
    else if (secondary.length > 0 && secondary !== username) customFields.push(csvColumnField(header[normalized.indexOf('secondary_username')]!, secondary));
    header.forEach((label, index) => {
      if (AVIRA_CONSUMED.has(normalized[index]!) || IGNORED_COLUMNS.has(normalized[index]!)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    if (get('name').length === 0 && website.length === 0 && username.length === 0 && password.length === 0 && customFields.length === 0) continue;
    const host = website.length > 0 ? serverHost(website).replace(/^www\./u, '') : '';
    rows.push({
      type: 'login',
      name: get('name') || host || website || username || 'Sans nom',
      username,
      password,
      uris: website.length > 0 ? [website] : [],
      notes: '',
      totpSecret: '',
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
    });
  }
  return rows;
}

/** Colonnes True Key de metadonnees, sans valeur pour l'utilisateur (couleur, reglages de remplissage, version d'export). */
const TRUEKEY_IGNORED = new Set(['kind', 'autologin', 'protectedwithpassword', 'subdomainonly', 'hexcolor', 'favorite', 'tk_export_version']);

/** Colonnes True Key consommees quel que soit le genre de la ligne : nom et notes. */
const TRUEKEY_COMMON = ['name', 'title', 'memo', 'note', 'document_content'];

/** Colonnes consommees selon le genre (`kind`) ; ailleurs, une valeur non vide devient un champ. */
const TRUEKEY_CONSUMED: Record<string, readonly string[]> = {
  login: ['url', 'login', 'password'],
  cc: ['cardholder', 'number', 'expirydate'],
  note: [],
};

/**
 * Expiration d'une carte True Key (`expiryDate`) : date ISO ou `MM/AA(AA)` ->
 * `MM/AAAA`, lue sans `Date` (un fuseau ne doit pas reculer l'expiration d'un
 * mois). `null` pour toute autre forme, gardee alors en champ.
 */
function trueKeyExpiry(raw: string): string | null {
  const iso = /^(\d{4})-(\d{1,2})(?:[-T ]|$)/u.exec(raw);
  if (iso !== null) return normalizeExpiry(`${iso[2]}/${iso[1]}`);
  return normalizeExpiry(raw);
}

/**
 * Export CSV de McAfee True Key (une seule feuille, colonne `kind` : `login`,
 * `cc`, `note`, ou piece d'identite), reconnu a `kind` accompagnee d'une
 * colonne propre a True Key (`autologin`, `protectedWithPassword`,
 * `tk_export_version`). Sans lui, le lecteur generique faisait de chaque carte
 * et de chaque note un identifiant, laissait le numero de carte EN CLAIR dans
 * un champ non masque, rognait le mot de passe et releguait `memo` en champ.
 * Ici : identifiants (mot de passe jamais rogne), cartes (titulaire, numero,
 * expiration), notes (`document_content`), et tout autre genre en note dont
 * chaque colonne non vide devient un champ — le numero d'une piece masque.
 * `null` si le CSV n'est pas de cette forme.
 */
function parseTrueKeyCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  const has = (name: string): boolean => normalized.includes(name);
  if (!(has('kind') && (has('autologin') || has('protectedwithpassword') || has('tk_export_version')))) return null;
  const rows: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const raw = (name: string): string => {
      const index = normalized.indexOf(name);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (name: string): string => raw(name).trim();
    const kind = get('kind').toLowerCase();
    const known = TRUEKEY_CONSUMED[kind];
    const consumed = new Set([...TRUEKEY_COMMON, ...(known ?? [])]);
    const type: VaultItemType = kind === 'login' ? 'login' : kind === 'cc' ? 'card' : 'note';
    const customFields: EntryCustomField[] = [];
    const typed: Record<string, string> = {};
    if (type === 'card') {
      typed['cardholder'] = get('cardholder');
      typed['number'] = get('number');
      const expiry = get('expirydate');
      const normalizedExpiry = trueKeyExpiry(expiry);
      if (normalizedExpiry !== null) typed['expiry'] = normalizedExpiry;
      else if (expiry.length > 0) customFields.push({ name: 'Expiration', value: expiry, hidden: false });
    }
    header.forEach((label, index) => {
      const column = normalized[index]!;
      if (consumed.has(column) || TRUEKEY_IGNORED.has(column) || IGNORED_COLUMNS.has(column)) return;
      const cell = (values[index] ?? '').trim();
      if (cell.length === 0) return;
      // Piece d'identite (genre inconnu) : son numero est masque comme celui d'une carte.
      if (column === 'number') customFields.push({ name: label, value: cell, hidden: true });
      else customFields.push(csvFreeColumnField(label, values[index] ?? '')!);
    });
    const url = type === 'login' ? get('url') : '';
    const username = type === 'login' ? get('login') : '';
    const password = type === 'login' ? raw('password') : '';
    const notes = joinNotes([raw('document_content'), get('note'), get('memo')]);
    const group = typedFieldGroup(type);
    const fields = group === null ? {} : normalizeTypedFields(group, typed);
    const titled = type === 'login' ? get('name') || get('title') : get('title') || get('name');
    if (titled.length === 0 && url.length === 0 && username.length === 0 && password.length === 0 && notes.length === 0 && customFields.length === 0 && Object.keys(fields).length === 0) continue;
    const host = url.length > 0 ? serverHost(url).replace(/^www\./u, '') : '';
    const favorite = get('favorite').toLowerCase();
    rows.push({
      type,
      name: titled || host || url || username || fields['cardholder'] || 'Sans nom',
      username,
      password,
      uris: url.length > 0 ? [url] : [],
      notes,
      totpSecret: '',
      favorite: favorite === 'true' || favorite === '1',
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(group !== null && Object.keys(fields).length > 0 ? { fields } : {}),
    });
  }
  return rows;
}

/** Colonnes KeePass consommees (en minuscules) ; toute autre colonne non vide devient un champ personnalise. */
const KEEPASS_CONSUMED = new Set(['group', 'title', 'username', 'password', 'url', 'notes', 'totp', 'tags']);

/** Colonnes KeePassXC de metadonnees, sans valeur pour l'utilisateur (icone, horodatages). */
const KEEPASSXC_IGNORED = new Set(['icon', 'last modified', 'created']);

/**
 * Export CSV a la forme KeePass (`Group,Title,Username,Password,URL,Notes[,TOTP]`) :
 * export « CSV (KeePass) » de Passbolt et export CSV de KeePassXC, reconnu a
 * `group` accompagnee de `title`, `password` et `url`. Sans lui, le lecteur
 * generique releguait le chemin du groupe en champ (aucun dossier recree),
 * semait l'icone et les horodatages KeePassXC en champs sur chaque entree et
 * rognait le mot de passe. Ici : le groupe devient le chemin de dossier — le
 * groupe racine que KeePassXC prefixe a chaque chemin (`Root/...`, reconnu a
 * ses colonnes `Icon` / `Last Modified` / `Created`) est retire, Passbolt
 * n'en ecrit pas —, le mot de passe n'est jamais rogne, la graine TOTP
 * (`otpauth://` compris) est reprise, une entree sans titre prend l'hote de
 * son adresse. Les references de champ `{REF:...}` (KeePassXC ecrit le texte
 * brut) se resolvent entre lignes du fichier comme dans une base KDBX ; l'export
 * CSV ne porte pas d'UUID, une reference `@I:` reste donc en texte et comptee.
 * `null` si le CSV n'est pas de cette forme.
 */
function parseKeePassCsv(
  header: readonly string[],
  normalized: readonly string[],
  cells: readonly (readonly string[])[],
  keePass1x?: KeePass1xExtras,
): ImportedEntry[] | null {
  const has = (name: string): boolean => normalized.includes(name);
  if (!(has('group') && has('title') && has('password') && has('url'))) return null;
  const keePassXc = has('icon') || has('last modified') || has('created');
  const free = (index: number): boolean => {
    const column = normalized[index]!;
    return !(KEEPASS_CONSUMED.has(column) || (keePassXc && KEEPASSXC_IGNORED.has(column)) || IGNORED_COLUMNS.has(column));
  };
  // Chaines KeePass de chaque ligne (champs natifs, colonnes libres par position) : cibles des references.
  const lines = cells.map((values, row) => {
    const raw = (name: string): string => {
      const index = normalized.indexOf(name);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const strings = new Map<string, string>([
      ['Title', raw('title').trim()],
      ['UserName', raw('username').trim()],
      ['Password', raw('password')],
      ['URL', raw('url').trim()],
      ['Notes', raw('notes')],
    ]);
    header.forEach((_, index) => {
      if (free(index)) strings.set(`colonne:${index}`, values[index] ?? '');
    });
    const target: ReferenceTarget = { uuid: String(row), strings, live: true };
    return { target, totp: raw('totp').trim(), group: raw('group').trim(), tags: raw('tags').trim() };
  });
  // Seul l'export de KeePass 1.x peut porter l'UUID (colonne `UUID` cochee) ; sinon `byUuid` vide, une reference `@I:` ne trouve rien.
  const byUuid = new Map<string, ReferenceTarget>();
  keePass1x?.uuids?.forEach((uuid, row) => {
    if (uuid.length > 0) byUuid.set(uuid.toUpperCase(), lines[row]!.target);
  });
  const index: ReferenceIndex = { entries: lines.map((line) => line.target), byUuid };
  const resolved = lines.map((line) => resolveEntryReferences(line.target.strings, line.target.uuid, index));
  // Seules les lignes designees par une autre portent un `sourceId` (jamais scelle) : la cible du lien.
  const designated = new Set(resolved.flatMap((result, row) => [...result.targets].filter((target) => target !== String(row))));
  const rows: ImportedEntry[] = [];
  lines.forEach((line, row) => {
    const { strings, targets, unresolved } = resolved[row]!;
    const get = (key: string): string => strings.get(key) ?? '';
    const url = get('URL');
    const username = get('UserName');
    const password = get('Password');
    const totp = normalizeTotpSecret(line.totp);
    const notes = joinNotes([get('Notes'), totp.extraNote]);
    const customFields: EntryCustomField[] = [];
    header.forEach((label, index) => {
      if (!free(index)) return;
      const field = csvFreeColumnField(label, get(`colonne:${index}`));
      if (field !== null) customFields.push(field);
    });
    const title = get('Title');
    if (title.length === 0 && url.length === 0 && username.length === 0 && password.length === 0 && notes.length === 0 && totp.secret.length === 0 && customFields.length === 0) return;
    const folder = folderPathOf(line.group);
    // KeePassXC ecrit le groupe racine en tete de chaque chemin : une entree a la racine porte `Root` seul.
    const folderPath = keePassXc ? folder.folderPath?.slice(1) : folder.folderPath;
    const urlHost = url.length > 0 ? serverHost(url).replace(/^www\./u, '') : '';
    const tags = parseTagList(line.tags);
    const linkedSourceIds = [...targets].filter((target) => target !== String(row)).map((target) => `keepass-csv:${target}`);
    rows.push({
      type: 'login',
      name: title || urlHost || url || username || 'Sans nom',
      username,
      password,
      uris: url.length > 0 ? [url] : [],
      notes,
      totpSecret: totp.secret,
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(tags.length > 0 ? { tags } : {}),
      ...(folderPath !== undefined && folderPath.length > 0 ? { folderPath } : {}),
      ...(designated.has(String(row)) ? { sourceId: `keepass-csv:${row}` } : {}),
      ...(linkedSourceIds.length > 0 ? { linkedSourceIds } : {}),
      ...(unresolved > 0 ? { unresolvedReferences: unresolved } : {}),
      ...keePass1x?.extras(row),
    });
  });
  return rows;
}

/** Colonnes titre, identifiant, mot de passe, adresse et notes, aux noms de la forme KeePass lue par `parseKeePassCsv` (groupe vide : l'export n'en porte pas). */
const KEEPASS_CSV_1X_AS_KEEPASS = ['group', 'title', 'username', 'password', 'url', 'notes'];

/**
 * Colonnes que l'export CSV de KeePass 1.x peut ecrire (`KeePassLibCpp/DataExchange/PwExport.cpp`), en minuscules,
 * dans l'ordre ou il les ecrit : l'utilisateur coche lesquelles ; cinq par defaut (`Account`, `Login Name`,
 * `Password`, `Web Site`, `Comments`), les seules qu'ecrit KeePass 2 (`KeePassCsv1x.cs`). Le lecteur generique ne reconnaissait que `Password` : groupe, dates, echeance et piece
 * jointe arrivaient en champs personnalises — la piece jointe en texte base64.
 */
const KEEPASS_1X_COLUMNS = [
  'group',
  'group tree',
  'account',
  'login name',
  'password',
  'web site',
  'comments',
  'uuid',
  'icon',
  'creation time',
  'last access',
  'last modification',
  'expires',
  'attachment description',
  'attachment',
];

/** Echeance « n'expire jamais » de KeePass 1.x (`g_pwTimeNever`, `PwManager.cpp`), ecrite comme une date. */
const KEEPASS_1X_NEVER_EXPIRES = '2999-12-28';

/**
 * En-tete d'un export CSV de KeePass 1.x (ou de KeePass 2 en « KeePass CSV (1.x) ») : des colonnes de cette liste,
 * sans doublon, dans son ordre, le mot de passe compris, et au moins un titre, un identifiant ou une adresse.
 */
function isKeePass1xHeader(normalized: readonly string[]): boolean {
  let last = -1;
  for (const name of normalized) {
    const index = KEEPASS_1X_COLUMNS.indexOf(name);
    if (index <= last) return false;
    last = index;
  }
  return normalized.includes('password') && ['account', 'login name', 'web site'].some((name) => normalized.includes(name));
}

/** Ce que l'export de KeePass 1.x porte en plus de la forme KeePass : UUID (cibles `@I:`) et complements par ligne. */
interface KeePass1xExtras {
  readonly uuids?: readonly string[];
  readonly extras: (row: number) => Partial<ImportedEntry>;
}

/**
 * Entrees d'un export CSV de KeePass 1.x, colonnes deja decoupees. Passe par `parseKeePassCsv` (references
 * `{REF:...}`, liens, titre de repli) : le chemin du groupe est `Group Tree` (les groupes parents, separes par `\`)
 * suivi de `Group` ; `UUID` rend les references `@I:` resolubles ; `Expires` devient l'echeance du mot de passe,
 * sauf « jamais » ; la piece jointe (`Attachment`, base64, nommee par `Attachment Description`) est reprise.
 * Icone et horodatages ne sont pas des donnees de l'utilisateur : ignores.
 *
 * @throws {Error} piece jointe illisible (base64 invalide)
 */
function parseKeePass1xCells(columns: readonly string[], rows: readonly (readonly string[])[]): ImportedEntry[] {
  const at = (row: readonly string[], name: string): string => {
    const index = columns.indexOf(name);
    return index === -1 ? '' : (row[index] ?? '');
  };
  const cells = rows.map((row) => {
    const group = at(row, 'group').trim();
    const tree = at(row, 'group tree').trim();
    const path = group.length > 0 && tree.length > 0 ? `${tree}\\${group}` : group;
    return [path, at(row, 'account'), at(row, 'login name'), at(row, 'password'), at(row, 'web site'), at(row, 'comments')];
  });
  const extras = (index: number): Partial<ImportedEntry> => {
    const row = rows[index]!;
    const expires = /^(\d{4}-\d{2}-\d{2})T/u.exec(at(row, 'expires').trim())?.[1];
    const encoded = at(row, 'attachment').replace(/\s+/gu, '');
    let bytes: Uint8Array | null = null;
    if (encoded.length > 0) {
      try {
        bytes = base64ToBytes(encoded);
      } catch {
        throw new Error(`export KeePass CSV (1.x) : piece jointe illisible a la ligne ${index + 2}`);
      }
    }
    return {
      ...(expires !== undefined && expires !== KEEPASS_1X_NEVER_EXPIRES ? { passwordExpiry: expires } : {}),
      ...(bytes !== null && bytes.length > 0 ? { attachments: [{ fileName: at(row, 'attachment description').trim() || 'piece-jointe', bytes }] } : {}),
    };
  };
  const uuids = columns.includes('uuid') ? rows.map((row) => at(row, 'uuid').trim()) : undefined;
  return parseKeePassCsv(KEEPASS_CSV_1X_AS_KEEPASS, KEEPASS_CSV_1X_AS_KEEPASS, cells, { ...(uuids !== undefined ? { uuids } : {}), extras })!;
}

/** Sequences de KeePass 1.x « encoder les retours a la ligne » (`\r`, `\n`) ; tout autre caractere echappe se relit tel quel. */
const KEEPASS_1X_ESCAPES: Readonly<Record<string, string>> = { r: '\r', n: '\n' };

/**
 * Colonnes et lignes d'un export CSV de KeePass 1.x ou « KeePass CSV (1.x) » de KeePass 2, ou `null` si le texte
 * n'en est pas un. Ce format n'est PAS du RFC 4180 : chaque champ est guillemete, `\` et `"` y sont echappes par
 * une barre oblique inverse (`WriteCsvString` de KeePass 2, `_ExpCsvStrIf` de KeePass 1.x), et un champ peut porter
 * des retours a la ligne — ou `\r` / `\n` si l'option « encoder les retours a la ligne » de KeePass 1.x est cochee.
 * Lu par le RFC 4180, un mot de passe contenant `"` fermait son champ trop tot et decalait toutes les colonnes
 * suivantes. Reconnu a sa ligne d'en-tete, chaque nom guillemete (`isKeePass1xHeader`). Lecture calquee sur celle
 * de KeePass (`ReadCsvField`) : hors guillemets, tout separateur est ignore ; autant de champs que de colonnes font
 * une entree.
 *
 * @throws {Error} derniere entree incomplete (fichier tronque)
 */
export function parseKeePassCsv1xRows(text: string): { columns: string[]; rows: string[][] } | null {
  const normalized = text.replace(/^\uFEFF/u, '').replace(/\r\n/gu, '\n');
  const headerLine = normalized.split('\n', 1)[0]!.trimEnd();
  if (!/^"[^"\\]+"(?:,"[^"\\]+")*$/u.test(headerLine)) return null;
  const columns = headerLine.slice(1, -1).split('","').map((name) => name.toLowerCase());
  if (!isKeePass1xHeader(columns)) return null;
  const fields: string[] = [];
  let current = '';
  let inField = false;
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i]!;
    if (char === '\\') {
      if (i + 1 >= normalized.length) throw new Error('export KeePass CSV (1.x) tronque');
      i++;
      if (inField) current += KEEPASS_1X_ESCAPES[normalized[i]!] ?? normalized[i]!;
    } else if (char === '"') {
      if (inField) {
        fields.push(current.replace(/\r\n?/gu, '\n'));
        current = '';
      }
      inField = !inField;
    } else if (inField) {
      current += char;
    }
  }
  if (inField || fields.length % columns.length !== 0) throw new Error('export KeePass CSV (1.x) tronque');
  const rows: string[][] = [];
  for (let start = columns.length; start < fields.length; start += columns.length) {
    rows.push(fields.slice(start, start + columns.length));
  }
  return { columns, rows };
}

/** Colonnes Buttercup consommees (en minuscules) ; toute autre colonne non vide devient un champ personnalise. */
const BUTTERCUP_CONSUMED = new Set(['!group_id', '!group_name', '!group_parent', 'title', 'username', 'password', 'url', 'note', 'notes']);

/** Colonnes Buttercup techniques, sans valeur pour l'utilisateur (identifiants internes). */
const BUTTERCUP_IGNORED = new Set(['id', '!type']);

/**
 * Export CSV de Buttercup (gestionnaire arrete, ses utilisateurs doivent
 * migrer), reconnu a `!group_id` ou `!group_name` accompagnee de `title` et
 * `password`. Sans lui, le lecteur generique semait `!group_id`,
 * `!group_name` et `id` en champs sur chaque entree (aucun dossier recree) et
 * rognait le mot de passe. Ici : le groupe devient le dossier — imbrique quand
 * l'export porte `!group_parent` (parent inconnu ou `0` = racine, cycle garde)
 * —, le mot de passe n'est jamais rogne, une propriete personnalisee dont la
 * valeur est une URI `otpauth://` exploitable devient la graine TOTP (Buttercup
 * range le 2FA ainsi, sous le nom choisi par l'utilisateur), une entree sans
 * titre prend l'hote de son adresse. `null` si le CSV n'est pas de cette forme.
 */
function parseButtercupCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  const has = (name: string): boolean => normalized.includes(name);
  if (!((has('!group_id') || has('!group_name')) && has('title') && has('password'))) return null;
  const rows = records.map((record) => parseCsvLine(record));
  const raw = (values: readonly string[], name: string): string => {
    const index = normalized.indexOf(name);
    return index === -1 ? '' : (values[index] ?? '');
  };
  const cell = (values: readonly string[], name: string): string => raw(values, name).trim();
  const groups = new Map<string, { name: string; parent: string }>();
  for (const values of rows) {
    const id = cell(values, '!group_id');
    if (id.length > 0 && !groups.has(id)) groups.set(id, { name: cell(values, '!group_name'), parent: cell(values, '!group_parent') });
  }
  const folderPathFor = (values: readonly string[]): readonly string[] => {
    const path = [cell(values, '!group_name')];
    const seen = new Set([cell(values, '!group_id')]);
    let parent = cell(values, '!group_parent');
    while (parent.length > 0 && parent !== '0' && !seen.has(parent)) {
      const group = groups.get(parent);
      if (group === undefined) break;
      seen.add(parent);
      path.unshift(group.name);
      parent = group.parent;
    }
    return path.filter((name) => name.length > 0);
  };
  const entries: ImportedEntry[] = [];
  for (const values of rows) {
    const url = cell(values, 'url');
    const username = cell(values, 'username');
    const password = raw(values, 'password');
    const notes = joinNotes([raw(values, 'note'), raw(values, 'notes')]);
    let totpSecret = '';
    const customFields: EntryCustomField[] = [];
    header.forEach((label, index) => {
      const column = normalized[index]!;
      if (BUTTERCUP_CONSUMED.has(column) || BUTTERCUP_IGNORED.has(column) || IGNORED_COLUMNS.has(column)) return;
      const content = (values[index] ?? '').trim();
      if (content.length === 0) return;
      if (totpSecret.length === 0 && /^otpauth:\/\//iu.test(content)) {
        const totp = normalizeTotpSecret(content);
        if (totp.secret.length > 0) {
          totpSecret = totp.secret;
          return;
        }
      }
      customFields.push(csvFreeColumnField(label, values[index] ?? '')!);
    });
    const title = cell(values, 'title');
    if (title.length === 0 && url.length === 0 && username.length === 0 && password.length === 0 && notes.length === 0 && totpSecret.length === 0 && customFields.length === 0) continue;
    const folderPath = folderPathFor(values);
    const siteHost = url.length > 0 ? serverHost(url).replace(/^www\./u, '') : '';
    entries.push({
      type: 'login',
      name: title || siteHost || url || username || 'Sans nom',
      username,
      password,
      uris: url.length > 0 ? [url] : [],
      notes,
      totpSecret,
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(folderPath.length > 0 ? { folderPath } : {}),
    });
  }
  return entries;
}

/** Champs Padloc reconnus par leur libelle (modeles par defaut, anglais et francais), en minuscules. */
const PADLOC_FIELDS = {
  username: ['username', 'user name', 'user', 'login', "nom d'utilisateur", 'nom d’utilisateur', 'identifiant'],
  email: ['email', 'e-mail', 'courriel'],
  password: ['password', 'mot de passe'],
  url: ['url', 'website', 'site web'],
  notes: ['note', 'notes'],
  totp: ['one-time password', 'totp', 'otp', 'mot de passe a usage unique', 'mot de passe à usage unique'],
  cardNumber: ['card number', 'numero de carte', 'numéro de carte'],
  cardholder: ['card owner', 'titulaire de la carte', 'titulaire'],
  expiry: ['valid until', "valable jusqu'au", 'expiration'],
  cvv: ['cvc', 'cvv'],
  pin: ['pin', 'code pin'],
} as const;

type PadlocField = keyof typeof PADLOC_FIELDS;

/** Champ Padloc d'un libelle de colonne ; `null` = champ libre (repris en champ personnalise). */
function padlocField(label: string): PadlocField | null {
  const key = label.trim().toLowerCase();
  for (const field of Object.keys(PADLOC_FIELDS) as PadlocField[]) {
    if ((PADLOC_FIELDS[field] as readonly string[]).includes(key)) return field;
  }
  return null;
}

/**
 * Export CSV de Padloc (gestionnaire arrete, ses utilisateurs doivent migrer) :
 * `name,tags` puis une colonne par libelle de champ rencontre dans le coffre —
 * Padloc n'a pas de colonnes fixes, chaque element ne remplit que celles de
 * ses propres champs. Reconnu a ses deux premieres colonnes `name` et `tags`,
 * lues par position (un champ Padloc peut lui-meme s'appeler « Name », ex. le
 * reseau d'un modele Wi-Fi). Sans lui, le lecteur generique rognait le mot de
 * passe, laissait la graine « One-Time Password » en champ (aucun code
 * genere) et faisait de chaque carte un identifiant au numero NON masque.
 * Ici : identifiant (l'e-mail a defaut), mot de passe jamais rogne, adresses,
 * notes, graine TOTP (base32 ou `otpauth://`), etiquettes reprises en
 * etiquettes ; une ligne portant un numero de carte sans identifiant ni mot de
 * passe devient une carte (titulaire, numero, expiration `AAAA-MM`, CVC,
 * PIN) ; une ligne sans autre champ que ses notes devient une note ; tout
 * autre champ non vide devient un champ personnalise, masque si son libelle
 * designe un secret. `null` si le CSV n'est pas de cette forme.
 */
function parsePadlocCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  if (!(normalized[0] === 'name' && normalized[1] === 'tags')) return null;
  const roles = header.map((label, index) => (index < 2 ? null : padlocField(label)));
  const entries: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const found: Partial<Record<PadlocField, string>> = {};
    const labelOf: Partial<Record<PadlocField, string>> = {};
    const uris: string[] = [];
    const notes: string[] = [];
    const extras: { label: string; value: string; role: PadlocField | null }[] = [];
    header.forEach((label, index) => {
      if (index < 2) return;
      const role = roles[index]!;
      const rawValue = values[index] ?? '';
      const value = rawValue.trim();
      if (value.length === 0) return;
      if (role === 'url') {
        if (!uris.includes(value)) uris.push(value);
      } else if (role === 'notes') {
        notes.push(rawValue);
      } else if (role !== null && found[role] === undefined) {
        // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
        found[role] = role === 'password' ? rawValue : value;
        labelOf[role] = label;
      } else {
        extras.push({ label, value, role });
      }
    });
    const name = (values[0] ?? '').trim();
    const tags = parseTagList(values[1] ?? '');
    const customFields: EntryCustomField[] = [];
    let totpSecret = '';
    const totp = normalizeTotpSecret(found.totp ?? '');
    if (totp.secret.length > 0) totpSecret = totp.secret;
    else if (found.totp !== undefined) customFields.push({ name: labelOf.totp!, value: found.totp, hidden: true });
    let username = found.username ?? '';
    if (username.length === 0) username = found.email ?? '';
    else if (found.email !== undefined && found.email !== username) customFields.push(csvColumnField(labelOf.email!, found.email));
    const password = found.password ?? '';
    const isCard = found.cardNumber !== undefined && username.length === 0 && password.length === 0;
    const typed: Record<string, string> = {};
    if (isCard) {
      typed['number'] = found.cardNumber!;
      typed['cardholder'] = found.cardholder ?? '';
      if (found.cvv !== undefined) typed['cvv'] = found.cvv;
      if (found.pin !== undefined) typed['pin'] = found.pin;
      const expiry = found.expiry ?? '';
      const normalizedExpiry = normalizeExpiry(expiry);
      if (normalizedExpiry !== null) typed['expiry'] = normalizedExpiry;
      else if (expiry.length > 0) customFields.push({ name: labelOf.expiry!, value: expiry, hidden: false });
    } else {
      // Hors carte, les champs de carte restent des champs : le numero et les codes masques.
      for (const role of ['cardNumber', 'cardholder', 'expiry', 'cvv', 'pin'] as const) {
        const value = found[role];
        if (value === undefined) continue;
        customFields.push({ name: labelOf[role]!, value, hidden: role === 'cardNumber' || role === 'cvv' || role === 'pin' });
      }
    }
    for (const extra of extras) {
      if (extra.role === 'totp' && totpSecret.length === 0) {
        const other = normalizeTotpSecret(extra.value);
        if (other.secret.length > 0) {
          totpSecret = other.secret;
          continue;
        }
      }
      const masked = extra.role === 'password' || extra.role === 'totp' || extra.role === 'cardNumber' || extra.role === 'cvv' || extra.role === 'pin';
      customFields.push(masked ? { name: extra.label, value: extra.value, hidden: true } : csvColumnField(extra.label, extra.value));
    }
    const noteText = joinNotes(notes);
    const fields = isCard ? normalizeTypedFields('card', typed) : {};
    const isNote = !isCard && username.length === 0 && password.length === 0 && uris.length === 0 && totpSecret.length === 0 && customFields.length === 0 && noteText.length > 0;
    if (name.length === 0 && uris.length === 0 && username.length === 0 && password.length === 0 && noteText.length === 0 && totpSecret.length === 0 && customFields.length === 0 && Object.keys(fields).length === 0) continue;
    const siteHost = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
    const type: VaultItemType = isCard ? 'card' : isNote ? 'note' : 'login';
    entries.push({
      type,
      name: name || siteHost || uris[0] || username || fields['cardholder'] || 'Sans nom',
      username,
      password,
      uris,
      notes: noteText,
      totpSecret,
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(isCard && Object.keys(fields).length > 0 ? { fields } : {}),
      ...(tags.length > 0 ? { tags } : {}),
    });
  }
  return entries;
}

/** Colonnes fixes d'un export CSV Codebook, toutes exigees pour le reconnaitre. */
const CODEBOOK_COLUMNS = { folder: 'category', favorite: 'favorite', name: 'entry', notes: 'note' } as const;

/** Libelles Codebook des roles d'une entree (comparaison sans casse) ; toute autre colonne est un champ. */
const CODEBOOK_ROLES = {
  username: /^(username|user name|login)$/iu,
  email: /^(e-?mail|e-?mail address)$/iu,
  password: /^password$/iu,
  url: /^(website|url|web site)$/iu,
  totp: /^(totp|otp|one[- ]?time password|2fa)$/iu,
  cardNumber: /^card number$/iu,
  cardholder: /^(name on card|card ?holder)$/iu,
  expiry: /^(expiration|expiration date|expiry|expiry date|expires)$/iu,
  cvv: /^(cvv|cvc|cvv2|security code)$/iu,
  pin: /^pin$/iu,
} as const;

type CodebookRole = keyof typeof CODEBOOK_ROLES;

/** Roles Codebook dont la valeur reste masquee quand elle devient un champ. */
const CODEBOOK_SECRET_ROLES: ReadonlySet<CodebookRole> = new Set(['password', 'totp', 'cardNumber', 'cvv', 'pin']);

/** Colonne `Account` de Codebook : numero de compte bancaire, masque comme un secret. */
const CODEBOOK_ACCOUNT = /^account( number)?$/iu;

/** Role d'une colonne Codebook, d'apres son libelle ; `null` s'il reste un champ personnalise. */
function codebookRole(label: string): CodebookRole | null {
  for (const role of Object.keys(CODEBOOK_ROLES) as CodebookRole[]) {
    if (CODEBOOK_ROLES[role].test(label.trim())) return role;
  }
  return null;
}

/**
 * Export CSV de **Codebook** (Zetetic, macOS / Windows / iOS / Android) :
 * `Category,Favorite,Entry,Note` puis une colonne par libelle de champ du
 * coffre (`Username`, `Email`, `Password`, `TOTP`, `Website`, `Phone`, `PIN`,
 * `Account`, `Date`...). Reconnu a ses quatre colonnes fixes, `Entry`
 * comprise (aucun autre export ne la porte). Sans lui, le lecteur generique
 * ecartait la categorie, rognait le mot de passe, laissait le compte bancaire
 * en clair et rangeait le nom de l'entree en champ « Entry ». Ici : nom,
 * categorie en dossier (un seul niveau, une `/` reste dans le nom), favori,
 * identifiant (l'e-mail a defaut, sinon garde en champ), mot de passe jamais
 * rogne, adresses, graine TOTP (base32 ou `otpauth://` ; illisible : champ
 * masque, jamais un TOTP faux) ; une ligne portant un numero de carte sans
 * identifiant ni mot de passe devient une carte ; une ligne sans identifiant,
 * mot de passe, adresse ni TOTP devient une note ; toute autre colonne non
 * vide devient un champ personnalise, masque si elle designe un secret (PIN,
 * compte...). `null` si le CSV n'est pas de cette forme.
 */
function parseCodebookCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  if (!Object.values(CODEBOOK_COLUMNS).every((column) => normalized.includes(column))) return null;
  const fixed = new Set<number>(Object.values(CODEBOOK_COLUMNS).map((column) => normalized.indexOf(column)));
  const roles = header.map((label, index) => (fixed.has(index) ? null : codebookRole(label)));
  const entries: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const get = (column: string): string => (values[normalized.indexOf(column)] ?? '').trim();
    const found: Partial<Record<CodebookRole, string>> = {};
    const labelOf: Partial<Record<CodebookRole, string>> = {};
    const uris: string[] = [];
    const extras: { label: string; value: string; secret: boolean }[] = [];
    header.forEach((label, index) => {
      if (fixed.has(index)) return;
      const role = roles[index]!;
      const rawCell = values[index] ?? '';
      const value = rawCell.trim();
      if (value.length === 0) return;
      if (role === 'url') {
        if (!uris.includes(value)) uris.push(value);
      } else if (role !== null && found[role] === undefined) {
        // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
        found[role] = role === 'password' ? rawCell : value;
        labelOf[role] = label;
      } else {
        extras.push({ label, value, secret: (role !== null && CODEBOOK_SECRET_ROLES.has(role)) || CODEBOOK_ACCOUNT.test(label.trim()) });
      }
    });
    const name = get(CODEBOOK_COLUMNS.name);
    const notes = get(CODEBOOK_COLUMNS.notes);
    const customFields: EntryCustomField[] = [];
    let username = found.username ?? '';
    if (username.length === 0 && found.email !== undefined) username = found.email;
    else if (found.email !== undefined && found.email !== username) customFields.push(csvColumnField(labelOf.email!, found.email));
    const password = found.password ?? '';
    let totp = { secret: '', extraNote: '' };
    if (found.totp !== undefined) {
      const seed = found.totp;
      if (/^otpauth:\/\//iu.test(seed) || /^[A-Z2-7]+=*$/u.test(seed.replace(/[\s-]+/gu, '').toUpperCase())) totp = normalizeTotpSecret(seed);
      else customFields.push({ name: labelOf.totp!, value: seed, hidden: true });
    }
    const isCard = found.cardNumber !== undefined && username.length === 0 && password.length === 0;
    const typed: Record<string, string> = {};
    if (isCard) {
      typed['number'] = found.cardNumber!;
      typed['cardholder'] = found.cardholder ?? '';
      for (const role of ['cvv', 'pin'] as const) {
        if (found[role] !== undefined) typed[role] = found[role]!;
      }
      const expiry = found.expiry ?? '';
      const readableExpiry = normalizeExpiry(expiry);
      if (readableExpiry !== null) typed['expiry'] = readableExpiry;
      else if (expiry.length > 0) customFields.push(csvColumnField(labelOf.expiry!, expiry));
    } else {
      // Hors carte, les champs de carte restent des champs : le numero et les codes masques.
      for (const role of ['cardNumber', 'cardholder', 'expiry', 'cvv', 'pin'] as const) {
        const value = found[role];
        if (value !== undefined) customFields.push({ name: labelOf[role]!, value, hidden: CODEBOOK_SECRET_ROLES.has(role) });
      }
    }
    for (const extra of extras) {
      const field = csvColumnField(extra.label, extra.value);
      customFields.push(extra.secret ? { ...field, hidden: true } : field);
    }
    const fields = isCard ? normalizeTypedFields('card', typed) : {};
    const noteText = joinNotes([notes, totp.extraNote]);
    if (name.length === 0 && noteText.length === 0 && uris.length === 0 && username.length === 0 && password.length === 0 && totp.secret.length === 0 && customFields.length === 0 && Object.keys(fields).length === 0) continue;
    const isNote = !isCard && uris.length === 0 && username.length === 0 && password.length === 0 && totp.secret.length === 0;
    const entryHost = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
    const favorite = get(CODEBOOK_COLUMNS.favorite).toLowerCase();
    const category = get(CODEBOOK_COLUMNS.folder);
    entries.push({
      type: isCard ? 'card' : isNote ? 'note' : 'login',
      name: name || entryHost || uris[0] || username || fields['cardholder'] || 'Sans nom',
      username,
      password,
      uris,
      notes: noteText,
      totpSecret: totp.secret,
      favorite: favorite === 'true' || favorite === '1' || favorite === 'yes',
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(isCard && Object.keys(fields).length > 0 ? { fields } : {}),
      // Categorie Codebook = un seul niveau : une `/` fait partie de son nom.
      ...(category.length > 0 ? { folderPath: [category] } : {}),
    });
  }
  return entries;
}

/** Colonnes d'un export CSV Netwrix Password Secure (en-tete allemand de l'outil), comparees sans casse. */
const NETWRIX_COLUMNS = {
  folder: 'organisationseinheit',
  name: 'beschreibung',
  username: 'benutzername',
  password: 'passwort',
  url: 'internetseite',
  notes: 'informationen',
  totp: 'one-time passwort',
} as const;

/** Colonne e-mail d'un formulaire Netwrix : identifiant a defaut, sinon gardee en champ. */
const NETWRIX_EMAIL = /^e-?mail$/iu;

/**
 * Export CSV de **Netwrix Password Secure** (ex-MATESO Password Safe,
 * editeur allemand) : separe par `;`, en-tete
 * `Organisationseinheit;Informationen;Beschreibung;Benutzername;Passwort;Internetseite;One-Time Passwort`
 * puis une colonne par champ propre au formulaire. Reconnu a ses colonnes
 * `Organisationseinheit` ET `Beschreibung`, propres a l'outil. Sans lui, le
 * fichier etait refuse (« aucune colonne reconnue » : lu a la virgule, tout
 * l'en-tete tenait dans une seule colonne). Ici : unite d'organisation en
 * dossier (niveaux separes par `/` ou `\`), nom, identifiant (l'e-mail a
 * defaut, sinon garde en champ), mot de passe jamais rogne, adresse, notes,
 * graine TOTP (base32 ou `otpauth://` ; illisible : champ masque, jamais un
 * TOTP faux) ; une ligne sans identifiant, mot de passe, adresse ni TOTP
 * devient une note ; toute autre colonne non vide devient un champ
 * personnalise, masque si elle designe un secret. `null` si le CSV n'est pas
 * de cette forme.
 */
function parseNetwrixCsv(records: readonly string[]): ImportedEntry[] | null {
  const header = parseCsvLine(records[0]!, ';').map((h) => h.trim());
  const normalized = header.map((h) => h.toLowerCase());
  if (!normalized.includes(NETWRIX_COLUMNS.folder) || !normalized.includes(NETWRIX_COLUMNS.name)) return null;
  const known = new Set<string>(Object.values(NETWRIX_COLUMNS));
  const emailIndex = header.findIndex((label) => NETWRIX_EMAIL.test(label));
  const entries: ImportedEntry[] = [];
  for (const record of records.slice(1)) {
    const values = parseCsvLine(record, ';');
    const raw = (column: string): string => values[normalized.indexOf(column)] ?? '';
    const get = (column: string): string => raw(column).trim();
    const customFields: EntryCustomField[] = [];
    const email = emailIndex === -1 ? '' : (values[emailIndex] ?? '').trim();
    let username = get(NETWRIX_COLUMNS.username);
    if (username.length === 0) username = email;
    else if (email.length > 0 && email !== username) customFields.push(csvColumnField(header[emailIndex]!, email));
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    const password = raw(NETWRIX_COLUMNS.password);
    const url = get(NETWRIX_COLUMNS.url);
    const uris = url.length > 0 ? [url] : [];
    let totp = { secret: '', extraNote: '' };
    const otp = get(NETWRIX_COLUMNS.totp);
    if (otp.length > 0) {
      if (/^otpauth:\/\//iu.test(otp) || /^[A-Z2-7]+=*$/u.test(otp.replace(/[\s-]+/gu, '').toUpperCase())) totp = normalizeTotpSecret(otp);
      else customFields.push({ name: header[normalized.indexOf(NETWRIX_COLUMNS.totp)]!, value: otp, hidden: true });
    }
    header.forEach((label, index) => {
      if (known.has(normalized[index]!) || index === emailIndex) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    const name = get(NETWRIX_COLUMNS.name);
    const notes = joinNotes([get(NETWRIX_COLUMNS.notes), totp.extraNote]);
    if (name.length === 0 && notes.length === 0 && uris.length === 0 && username.length === 0 && password.length === 0 && totp.secret.length === 0 && customFields.length === 0) continue;
    const isNote = uris.length === 0 && username.length === 0 && password.length === 0 && totp.secret.length === 0;
    const entryHost = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
    entries.push({
      type: isNote ? 'note' : 'login',
      name: name || entryHost || uris[0] || username || 'Sans nom',
      username,
      password,
      uris,
      notes,
      totpSecret: totp.secret,
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...folderPathOf(get(NETWRIX_COLUMNS.folder)),
    });
  }
  return entries;
}

/** Colonnes d'un export CSV Encryptr, comparees sans casse. */
const ENCRYPTR_COLUMNS = {
  type: 'entry type',
  name: 'label',
  username: 'username',
  password: 'password',
  url: 'site url',
  notes: 'notes',
  text: 'text',
  cardholder: 'name on card',
  cardNumber: 'card number',
  cvv: 'cvv',
  expiry: 'expiry',
} as const;

/** Colonnes de carte Encryptr dont la valeur reste masquee quand elle devient un champ. */
const ENCRYPTR_CARD_SECRETS: ReadonlySet<string> = new Set([ENCRYPTR_COLUMNS.cardNumber, ENCRYPTR_COLUMNS.cvv]);

/**
 * Export CSV d'**Encryptr** (SpiderOak, zero-knowledge) :
 * `Entry Type,Label,Username,Password,Site URL,Notes,Text,Name on card,Card Number,CVV,Expiry`,
 * une ligne par entree de type `Password`, `Credit Card` ou `General`.
 * Reconnu a ses colonnes `Entry Type` ET `Label`, propres a l'outil. Sans
 * lui, le lecteur generique nommait l'entree d'apres rien (`Label` et
 * `Site URL` inconnus), perdait l'adresse en champ, rangeait les cartes en
 * identifiants vides et laissait numero et CVV en champs visibles. Ici : nom,
 * identifiant, mot de passe jamais rogne, adresse, notes (`Notes` puis
 * `Text`) ; `Credit Card` devient une carte (titulaire, numero, CVV,
 * expiration ; illisible : gardee en champ) ; `General` devient une note ;
 * un type inconnu reste un identifiant s'il porte identifiant, mot de passe
 * ou adresse, sinon une note ; une carte ou une note garde identifiant, mot
 * de passe (masque) et adresse en champs ; hors carte, les colonnes de carte non vides
 * restent des champs (numero et CVV masques) ; toute autre colonne non vide
 * devient un champ personnalise, masque si elle designe un secret. `null` si
 * le CSV n'est pas de cette forme.
 */
function parseEncryptrCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  if (!normalized.includes(ENCRYPTR_COLUMNS.type) || !normalized.includes(ENCRYPTR_COLUMNS.name)) return null;
  const known = new Set<string>(Object.values(ENCRYPTR_COLUMNS));
  const cardColumns = [ENCRYPTR_COLUMNS.cardholder, ENCRYPTR_COLUMNS.cardNumber, ENCRYPTR_COLUMNS.cvv, ENCRYPTR_COLUMNS.expiry] as const;
  const entries: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const raw = (column: string): string => {
      const index = normalized.indexOf(column);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (column: string): string => raw(column).trim();
    const labelOf = (column: string): string => header[normalized.indexOf(column)]!;
    const declared = get(ENCRYPTR_COLUMNS.type).toLowerCase();
    const customFields: EntryCustomField[] = [];
    const isCard = declared === 'credit card';
    const typed: Record<string, string> = {};
    if (isCard) {
      typed['cardholder'] = get(ENCRYPTR_COLUMNS.cardholder);
      typed['number'] = get(ENCRYPTR_COLUMNS.cardNumber);
      typed['cvv'] = get(ENCRYPTR_COLUMNS.cvv);
      const expiry = get(ENCRYPTR_COLUMNS.expiry);
      const readableExpiry = normalizeExpiry(expiry);
      if (readableExpiry !== null) typed['expiry'] = readableExpiry;
      else if (expiry.length > 0) customFields.push(csvColumnField(labelOf(ENCRYPTR_COLUMNS.expiry), expiry));
    } else {
      // Hors carte, les colonnes de carte restent des champs : le numero et le CVV masques.
      for (const column of cardColumns) {
        const value = get(column);
        if (value.length > 0) customFields.push({ name: labelOf(column), value, hidden: ENCRYPTR_CARD_SECRETS.has(column) });
      }
    }
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    const password = raw(ENCRYPTR_COLUMNS.password);
    const username = get(ENCRYPTR_COLUMNS.username);
    const url = get(ENCRYPTR_COLUMNS.url);
    const uris = url.length > 0 ? [url] : [];
    const hasLogin = username.length > 0 || password.length > 0 || uris.length > 0;
    const isLogin = !isCard && declared !== 'general' && hasLogin;
    if (!isLogin && hasLogin) {
      // Une carte ou une note ne porte ni identifiant ni adresse : gardes en champs, le mot de passe masque.
      if (username.length > 0) customFields.push(csvColumnField(labelOf(ENCRYPTR_COLUMNS.username), username));
      if (password.length > 0) customFields.push({ name: labelOf(ENCRYPTR_COLUMNS.password), value: password, hidden: true });
      if (url.length > 0) customFields.push(csvColumnField(labelOf(ENCRYPTR_COLUMNS.url), url));
    }
    header.forEach((label, index) => {
      if (known.has(normalized[index]!)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    const name = get(ENCRYPTR_COLUMNS.name);
    const notes = joinNotes([get(ENCRYPTR_COLUMNS.notes), get(ENCRYPTR_COLUMNS.text)]);
    const fields = isCard ? normalizeTypedFields('card', typed) : {};
    if (name.length === 0 && notes.length === 0 && !hasLogin && customFields.length === 0 && Object.keys(fields).length === 0) continue;
    const entryHost = isLogin && uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
    entries.push({
      type: isCard ? 'card' : isLogin ? 'login' : 'note',
      name: name || entryHost || (isLogin ? uris[0] || username : '') || fields['cardholder'] || 'Sans nom',
      username: isLogin ? username : '',
      password: isLogin ? password : '',
      uris: isLogin ? uris : [],
      notes,
      totpSecret: '',
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(isCard && Object.keys(fields).length > 0 ? { fields } : {}),
    });
  }
  return entries;
}

/** Colonnes d'un export CSV RememBear, comparees sans casse. */
const REMEMBEAR_COLUMNS = {
  trash: 'trash',
  name: 'name',
  type: 'type',
  url: 'website',
  username: 'username',
  password: 'password',
  notes: 'notes',
  cardholder: 'cardholder',
  cardNumber: 'number',
  cvv: 'verification',
  expiryMonth: 'expirymonth',
  expiryYear: 'expiryyear',
  pin: 'pin',
} as const;

/** Colonnes de carte RememBear dont la valeur reste masquee quand elle devient un champ. */
const REMEMBEAR_CARD_SECRETS: ReadonlySet<string> = new Set([REMEMBEAR_COLUMNS.cardNumber, REMEMBEAR_COLUMNS.cvv, REMEMBEAR_COLUMNS.pin]);

/**
 * Export CSV de **RememBear** (TunnelBear) : une ligne par element, colonnes
 * `trash`, `name`, `type` (`LoginItem`, `CreditCardItem`...), `website`,
 * `username`, `password`, `notes` et colonnes de carte `cardholder`, `number`,
 * `verification`, `expiryMonth`, `expiryYear`, `pin`, `zipCode`. Reconnu a ses
 * colonnes `trash` ET `type`. Sans lui, le lecteur generique importait la
 * corbeille, rangeait les cartes en identifiants vides et laissait numero et
 * cryptogramme (`verification`) en champs VISIBLES. Ici : element a la
 * corbeille ignore ; `LoginItem` = identifiant (nom, identifiant, mot de passe
 * jamais rogne, adresse) ; `CreditCardItem` = carte (titulaire, numero, CVV,
 * PIN, expiration recousue de mois et annee ; illisible : gardee en champs) ;
 * un autre type reste un identifiant s'il porte identifiant, mot de passe ou
 * adresse, sinon une note ; une carte garde identifiant, mot de passe
 * (masque) et adresse en champs ; hors carte, les colonnes de carte non
 * vides restent des champs (numero, CVV et PIN masques) ; toute autre colonne
 * non vide (`zipCode`...) devient un champ personnalise, masque si elle
 * designe un secret. `null` si le CSV n'est pas de cette forme.
 */
function parseRememBearCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  if (!normalized.includes(REMEMBEAR_COLUMNS.trash) || !normalized.includes(REMEMBEAR_COLUMNS.type)) return null;
  const known = new Set<string>(Object.values(REMEMBEAR_COLUMNS));
  const cardColumns = [
    REMEMBEAR_COLUMNS.cardholder,
    REMEMBEAR_COLUMNS.cardNumber,
    REMEMBEAR_COLUMNS.cvv,
    REMEMBEAR_COLUMNS.expiryMonth,
    REMEMBEAR_COLUMNS.expiryYear,
    REMEMBEAR_COLUMNS.pin,
  ] as const;
  const entries: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const raw = (column: string): string => {
      const index = normalized.indexOf(column);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (column: string): string => raw(column).trim();
    const labelOf = (column: string): string => header[normalized.indexOf(column)]!;
    // La corbeille RememBear n'est pas reprise : l'utilisateur l'avait jetee.
    if (get(REMEMBEAR_COLUMNS.trash).toLowerCase() === 'true') continue;
    const declared = get(REMEMBEAR_COLUMNS.type).toLowerCase();
    const customFields: EntryCustomField[] = [];
    const isCard = declared === 'creditcarditem';
    const typed: Record<string, string> = {};
    if (isCard) {
      typed['cardholder'] = get(REMEMBEAR_COLUMNS.cardholder);
      typed['number'] = get(REMEMBEAR_COLUMNS.cardNumber);
      typed['cvv'] = get(REMEMBEAR_COLUMNS.cvv);
      typed['pin'] = get(REMEMBEAR_COLUMNS.pin);
      const month = get(REMEMBEAR_COLUMNS.expiryMonth);
      const year = get(REMEMBEAR_COLUMNS.expiryYear);
      const readableExpiry = month.length > 0 && year.length > 0 ? normalizeExpiry(`${month}/${year}`) : null;
      if (readableExpiry !== null) typed['expiry'] = readableExpiry;
      else {
        // Expiration incomplete ou illisible : mois et annee gardes tels quels, jamais perdus.
        if (month.length > 0) customFields.push(csvColumnField(labelOf(REMEMBEAR_COLUMNS.expiryMonth), month));
        if (year.length > 0) customFields.push(csvColumnField(labelOf(REMEMBEAR_COLUMNS.expiryYear), year));
      }
    } else {
      // Hors carte, les colonnes de carte restent des champs : numero, CVV et PIN masques.
      for (const column of cardColumns) {
        const value = get(column);
        if (value.length > 0) customFields.push({ name: labelOf(column), value, hidden: REMEMBEAR_CARD_SECRETS.has(column) });
      }
    }
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    const password = raw(REMEMBEAR_COLUMNS.password);
    const username = get(REMEMBEAR_COLUMNS.username);
    const url = get(REMEMBEAR_COLUMNS.url);
    const uris = url.length > 0 ? [url] : [];
    const hasLogin = username.length > 0 || password.length > 0 || uris.length > 0;
    const isLogin = !isCard && (declared === 'loginitem' || hasLogin);
    if (!isLogin && hasLogin) {
      // Une carte ne porte ni identifiant ni adresse : gardes en champs, le mot de passe masque.
      if (username.length > 0) customFields.push(csvColumnField(labelOf(REMEMBEAR_COLUMNS.username), username));
      if (password.length > 0) customFields.push({ name: labelOf(REMEMBEAR_COLUMNS.password), value: password, hidden: true });
      if (url.length > 0) customFields.push(csvColumnField(labelOf(REMEMBEAR_COLUMNS.url), url));
    }
    header.forEach((label, index) => {
      if (known.has(normalized[index]!)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    const name = get(REMEMBEAR_COLUMNS.name);
    const notes = get(REMEMBEAR_COLUMNS.notes);
    const fields = isCard ? normalizeTypedFields('card', typed) : {};
    if (name.length === 0 && notes.length === 0 && !hasLogin && customFields.length === 0 && Object.keys(fields).length === 0) continue;
    const entryHost = isLogin && uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
    entries.push({
      type: isCard ? 'card' : isLogin ? 'login' : 'note',
      name: name || entryHost || (isLogin ? uris[0] || username : '') || fields['cardholder'] || 'Sans nom',
      username: isLogin ? username : '',
      password: isLogin ? password : '',
      uris: isLogin ? uris : [],
      notes,
      totpSecret: '',
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(isCard && Object.keys(fields).length > 0 ? { fields } : {}),
    });
  }
  return entries;
}

/** Colonnes d'un export CSV Passpack, comparees sans casse. */
const PASSPACK_COLUMNS = {
  name: 'entry name',
  username: 'user id',
  password: 'password',
  url: 'url',
  tags: 'tags',
  notes: 'notes',
  sharedNotes: 'shared notes',
  extraFields: 'extra fields',
  email: 'email',
} as const;

/** Type Passpack d'un champ supplementaire dont la valeur est un secret. */
const PASSPACK_SECRET_TYPE = /pass|secret|hidden/iu;

/** Lit une valeur JSON ; `undefined` si le texte n'en est pas une. */
function parseJsonOrUndefined(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

/** Element d'une liste Passpack : objet, ou objet ecrit en chaine JSON. */
function passpackListItem(item: unknown): unknown {
  if (typeof item !== 'string') return item;
  const parsed = parseJsonOrUndefined(item);
  return parsed !== null && typeof parsed === 'object' ? parsed : item;
}

/**
 * Colonne `Tags` d'un export Passpack : `{"tags":["{\"tag\":\"Travail\",...}"]}`
 * (chaque etiquette en chaine JSON, comme le lit Bitwarden), ou simple liste
 * texte des exports anciens. Jamais le JSON brut decoupe en fausses etiquettes.
 */
export function parsePasspackTags(raw: string): string[] {
  const text = raw.trim();
  if (text.length === 0) return [];
  const parsed = parseJsonOrUndefined(text);
  if (parsed === null || typeof parsed !== 'object' || !Array.isArray((parsed as { tags?: unknown }).tags)) return parseTagList(text);
  return normalizeTags(
    (parsed as { tags: unknown[] }).tags.map((item) => {
      const tag = passpackListItem(item);
      if (typeof tag === 'string') return tag;
      return tag !== null && typeof tag === 'object' ? (tag as { tag?: unknown }).tag : undefined;
    }),
  );
}

/**
 * Colonne `Extra Fields` d'un export Passpack :
 * `{"extraFields":["{\"name\":\"PIN\",\"data\":\"1234\",\"type\":...}"]}`.
 * Un champ par element non vide, masque si son type ou son libelle designe un
 * secret ; sa valeur n'est jamais rognee. Contenu illisible : garde tel quel
 * en UN champ masque (il peut porter un secret), jamais perdu ni affiche.
 */
export function parsePasspackExtraFields(label: string, raw: string): EntryCustomField[] {
  if (raw.trim().length === 0) return [];
  const parsed = parseJsonOrUndefined(raw.trim());
  const list = parsed !== null && typeof parsed === 'object' ? (parsed as { extraFields?: unknown }).extraFields : undefined;
  if (!Array.isArray(list)) return [{ name: label, value: raw, hidden: true }];
  const fields: EntryCustomField[] = [];
  for (const item of list) {
    const field = passpackListItem(item);
    if (field === null || typeof field !== 'object') {
      if (typeof field === 'string' && field.trim().length > 0) fields.push({ name: label, value: field, hidden: true });
      continue;
    }
    const { name, data, type } = field as { name?: unknown; data?: unknown; type?: unknown };
    const value = typeof data === 'string' ? data : typeof data === 'number' ? String(data) : '';
    if (value.trim().length === 0) continue;
    const fieldName = typeof name === 'string' ? name.trim() : '';
    const custom = csvColumnField(fieldName, value);
    fields.push(custom.hidden || PASSPACK_SECRET_TYPE.test(String(type ?? '')) ? { ...custom, hidden: true } : custom);
  }
  return fields;
}

/**
 * Export CSV de **Passpack** : une ligne par entree, colonnes `Entry Name`,
 * `User ID`, `Password`, `URL`, `Tags`, `Notes`, `Email`, `Shared Notes`,
 * `Extra Fields`. Reconnu a ses colonnes `Entry Name` ET `User ID`. Sans lui,
 * le lecteur generique perdait le nom (entree nommee d'apres son adresse),
 * rangeait l'identifiant en champ, decoupait le JSON des etiquettes en fausses
 * etiquettes et affichait EN CLAIR le JSON des champs supplementaires. Ici :
 * nom, identifiant (l'e-mail a defaut, sinon garde en champ), mot de passe
 * jamais rogne, adresse ; etiquettes reprises ; notes et notes partagees
 * recousues, cellules hors en-tete ajoutees aux notes ; champs
 * supplementaires decoupes (masques s'ils designent un secret) ; entree sans
 * identifiant, mot de passe ni adresse = note ; toute autre colonne non vide
 * devient un champ personnalise, masque si elle designe un secret. `null` si
 * le CSV n'est pas de cette forme.
 */
function parsePasspackCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  if (!normalized.includes(PASSPACK_COLUMNS.name) || !normalized.includes(PASSPACK_COLUMNS.username)) return null;
  const known = new Set<string>(Object.values(PASSPACK_COLUMNS));
  const entries: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const raw = (column: string): string => {
      const index = normalized.indexOf(column);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (column: string): string => raw(column).trim();
    const labelOf = (column: string): string => header[normalized.indexOf(column)]!;
    const customFields: EntryCustomField[] = [];
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    const password = raw(PASSPACK_COLUMNS.password);
    const userId = get(PASSPACK_COLUMNS.username);
    const email = get(PASSPACK_COLUMNS.email);
    const username = userId || email;
    if (userId.length > 0 && email.length > 0 && email !== userId) customFields.push(csvColumnField(labelOf(PASSPACK_COLUMNS.email), email));
    const url = get(PASSPACK_COLUMNS.url);
    const uris = url.length > 0 ? [url] : [];
    if (normalized.includes(PASSPACK_COLUMNS.extraFields)) {
      customFields.push(...parsePasspackExtraFields(labelOf(PASSPACK_COLUMNS.extraFields), raw(PASSPACK_COLUMNS.extraFields)));
    }
    header.forEach((label, index) => {
      if (known.has(normalized[index]!)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    // Cellules au-dela de l'en-tete (virgule non guillemetee) : ajoutees aux notes, comme Bitwarden.
    const overflow = values.slice(header.length).map((value) => value.trim());
    const notes = joinNotes([get(PASSPACK_COLUMNS.notes), get(PASSPACK_COLUMNS.sharedNotes), ...overflow]);
    const tags = parsePasspackTags(raw(PASSPACK_COLUMNS.tags));
    const name = get(PASSPACK_COLUMNS.name);
    const isLogin = username.length > 0 || password.length > 0 || uris.length > 0;
    if (name.length === 0 && notes.length === 0 && !isLogin && customFields.length === 0) continue;
    const entryHost = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
    entries.push({
      type: isLogin ? 'login' : 'note',
      name: name || entryHost || uris[0] || username || 'Sans nom',
      username,
      password,
      uris,
      notes,
      totpSecret: '',
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(tags.length > 0 ? { tags } : {}),
    });
  }
  return entries;
}

/** Colonnes d'un export CSV Blur (Abine), comparees sans casse. */
const BLUR_COLUMNS = {
  domain: 'domain',
  email: 'email',
  username: 'username',
  password: 'password',
  label: 'label',
} as const;

/** Libelle absent ecrit en toutes lettres par Blur. */
const BLUR_NULL_LABEL = 'null';

/**
 * Export CSV de **Blur** (Abine) : une ligne par identifiant, colonnes
 * `domain`, `email`, `username`, `password`, `label`. Reconnu a ses colonnes
 * `domain` ET `label`. Sans lui, le lecteur generique rangeait le domaine et
 * le libelle en champs : l'entree n'avait plus d'adresse (le remplissage ne
 * la proposait sur aucun site) et prenait le nom de son identifiant. Ici :
 * nom = libelle (`null` ecrit par Blur = absent), sinon le domaine ;
 * adresse = domaine ; identifiant = l'e-mail (Blur sert surtout des e-mails
 * masques), l'identifiant a defaut, et un identifiant distinct de l'e-mail
 * garde en champ ; mot de passe jamais rogne ; entree sans identifiant, mot
 * de passe ni domaine = note ; toute autre colonne non vide devient un champ
 * personnalise, masque si elle designe un secret. `null` si le CSV n'est pas
 * de cette forme.
 */
function parseBlurCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  if (!normalized.includes(BLUR_COLUMNS.domain) || !normalized.includes(BLUR_COLUMNS.label)) return null;
  const known = new Set<string>(Object.values(BLUR_COLUMNS));
  const entries: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const raw = (column: string): string => {
      const index = normalized.indexOf(column);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (column: string): string => raw(column).trim();
    const labelOf = (column: string): string => header[normalized.indexOf(column)]!;
    const customFields: EntryCustomField[] = [];
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    const password = raw(BLUR_COLUMNS.password);
    const email = get(BLUR_COLUMNS.email);
    const login = get(BLUR_COLUMNS.username);
    const username = email || login;
    if (email.length > 0 && login.length > 0 && login !== email) customFields.push(csvColumnField(labelOf(BLUR_COLUMNS.username), login));
    const domain = get(BLUR_COLUMNS.domain);
    const uris = domain.length > 0 ? [domain] : [];
    header.forEach((label, index) => {
      if (known.has(normalized[index]!)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    const declaredLabel = get(BLUR_COLUMNS.label);
    const name = declaredLabel.toLowerCase() === BLUR_NULL_LABEL ? '' : declaredLabel;
    const isLogin = username.length > 0 || password.length > 0 || uris.length > 0;
    if (name.length === 0 && !isLogin && customFields.length === 0) continue;
    const entryHost = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
    entries.push({
      type: isLogin ? 'login' : 'note',
      name: name || entryHost || uris[0] || username || 'Sans nom',
      username,
      password,
      uris,
      notes: '',
      totpSecret: '',
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
    });
  }
  return entries;
}

/** Colonnes d'un export CSV d'identifiants a colonnes nommees, comparees sans casse ; toutes exigees. */
interface LoginCsvColumns {
  /** Absente : le nom vient de l'hote de l'adresse (SaferPass). */
  readonly name?: string;
  readonly username: string;
  readonly password: string;
  readonly url: string;
  readonly notes?: string;
  /** Colonne de groupe / categorie : un dossier (`/` ou `\\` separent les niveaux, `folderPathOf`). */
  readonly folder?: string;
  /** Colonnes de notes FACULTATIVES (absentes d'une version de l'export), ajoutees aux notes quand elles sont la. */
  readonly moreNotes?: readonly string[];
  /** L'en-tete doit porter ces colonnes et AUCUNE autre : format trop commun pour etre reconnu a un sous-ensemble. */
  readonly exact?: boolean;
}

/** Export CSV de Meldium : `DisplayName,Notes,Url,UserName,Password`. */
const MELDIUM_COLUMNS: LoginCsvColumns = { name: 'displayname', username: 'username', password: 'password', url: 'url', notes: 'notes' };

/** Export CSV de Yoti : `Name,URL,User name,Password`. */
const YOTI_COLUMNS: LoginCsvColumns = { name: 'name', username: 'user name', password: 'password', url: 'url' };

/**
 * Export CSV de LogMeOnce : `name,url,username,password,group,extra` (ancien) ou
 * `name,url,note,group,username,password,passkey,extra` (actuel). `group` = dossier ;
 * `note` et `extra` = notes, l'une ou l'autre selon la version.
 */
const LOGMEONCE_COLUMNS: LoginCsvColumns = {
  name: 'name',
  username: 'username',
  password: 'password',
  url: 'url',
  folder: 'group',
  moreNotes: ['note', 'extra'],
};

/** Export CSV de PassKeep : `category,title,username,password,site,description,password2` (2e mot de passe = champ masque). */
const PASSKEEP_COLUMNS: LoginCsvColumns = {
  name: 'title',
  username: 'username',
  password: 'password',
  url: 'site',
  notes: 'description',
  folder: 'category',
};

/** Export CSV de SaferPass : `url,username,password,notes`, sans nom (celui-ci vient de l'hote), et rien d'autre. */
const SAFERPASS_COLUMNS: LoginCsvColumns = { username: 'username', password: 'password', url: 'url', notes: 'notes', exact: true };

/**
 * Exports CSV **Meldium**, **Yoti**, **LogMeOnce**, **PassKeep** et **SaferPass** :
 * une ligne par identifiant, colonnes nommees propres a chaque outil (forme lue
 * par les importeurs Bitwarden).
 * Reconnus a TOUTES leurs colonnes. Sans eux, le lecteur generique rangeait
 * `DisplayName` (Meldium) et `User name` (Yoti) en champs : l'entree Meldium
 * prenait le nom de son adresse, l'entree Yoti n'avait plus d'identifiant (le
 * remplissage ne connaissait plus le compte), et les mots de passe etaient
 * rognes ; LogMeOnce et PassKeep perdaient leurs dossiers (groupe en champ,
 * categorie ignoree), PassKeep son adresse et sa description (en champs),
 * SaferPass nommait chaque entree de son adresse entiere. Ici : nom = colonne
 * de nom, sinon l'hote de l'adresse ; groupe / categorie = dossier ; mot de passe
 * jamais rogne ; ligne sans identifiant, mot de passe ni adresse = note ;
 * ligne vide ignoree ; toute autre colonne non vide devient un champ
 * personnalise, masque si elle designe un secret. `null` si le CSV n'est pas
 * de cette forme.
 */
function parseNamedLoginCsv(
  format: LoginCsvColumns,
  header: readonly string[],
  normalized: readonly string[],
  records: readonly string[],
): ImportedEntry[] | null {
  const required = [format.name, format.username, format.password, format.url, format.notes, format.folder].filter(
    (column): column is string => column !== undefined,
  );
  for (const column of required) if (!normalized.includes(column)) return null;
  const known = new Set<string>([...required, ...(format.moreNotes ?? [])]);
  if (format.exact === true && !normalized.every((column) => known.has(column))) return null;
  const entries: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const raw = (column: string | undefined): string => {
      const index = column === undefined ? -1 : normalized.indexOf(column);
      return index === -1 ? '' : (values[index] ?? '');
    };
    const get = (column: string | undefined): string => raw(column).trim();
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    const password = raw(format.password);
    const username = get(format.username);
    const url = get(format.url);
    const uris = url.length > 0 ? [url] : [];
    const notes = joinNotes([get(format.notes), ...(format.moreNotes ?? []).map(get)]);
    const customFields: EntryCustomField[] = [];
    header.forEach((label, index) => {
      if (known.has(normalized[index]!)) return;
      const field = csvFreeColumnField(label, values[index] ?? '');
      if (field !== null) customFields.push(field);
    });
    const name = get(format.name);
    const isLogin = username.length > 0 || password.length > 0 || uris.length > 0;
    if (name.length === 0 && !isLogin && notes.length === 0 && customFields.length === 0) continue;
    const entryHost = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
    entries.push({
      type: isLogin ? 'login' : 'note',
      name: name || entryHost || uris[0] || username || 'Sans nom',
      username,
      password,
      uris,
      notes,
      totpSecret: '',
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...folderPathOf(get(format.folder)),
    });
  }
  return entries;
}

/** Champ d'un secret Delinea Secret Server : `slug` = nom technique du modele (`username`, `url`...), `label` = nom affiche. */
export interface DelineaSecretItem {
  readonly slug: string;
  readonly label: string;
  readonly value: string;
}

/** Un secret Delinea Secret Server (ex-Thycotic), lu d'un export CSV ou XML. */
export interface DelineaSecret {
  readonly name: string;
  /** Chemin du dossier, niveaux separes par `\` (`\Finance\Banques`). */
  readonly folder: string;
  readonly items: readonly DelineaSecretItem[];
}

/** Slugs Delinea repris par un role de l'entree ; tout autre champ devient un champ personnalise. */
const DELINEA_ROLES = ['username', 'url', 'password', 'notes', 'totp-key'] as const;

type DelineaRole = (typeof DELINEA_ROLES)[number];

/** Slugs Delinea d'un secret, au-dela des libelles de `SECRET_COLUMN` : cle privee, phrase de passe, codes de secours. */
const DELINEA_SECRET_SLUG = /pass|secret|key|pin|otp|token|backup-codes/u;

/** Slug d'une colonne CSV Delinea : `Date Created` -> `date-created`, comme le `<Slug>` de l'export XML. */
export function delineaSlug(label: string): string {
  return label.trim().toLowerCase().replace(/\s+/gu, '-');
}

/**
 * Entree d'un secret Delinea Secret Server. Bitwarden ne fait un identifiant
 * que d'un secret portant une adresse, et range le compte Windows, Active
 * Directory ou SSH (identifiant + mot de passe, sans adresse) en NOTE, son
 * mot de passe en champ : ici tout secret portant un identifiant, un mot de
 * passe, une adresse ou une graine TOTP reste un identifiant. Mot de passe
 * jamais rogne ; graine TOTP (`TOTP Key`, base32 ou `otpauth://`) reprise en
 * TOTP, illisible : champ masque, jamais un TOTP faux ; codes de secours,
 * cle privee et phrase de passe masques ; tout autre champ non vide garde en
 * champ personnalise sous son nom affiche ; dossier `\A\B` = dossier a
 * niveaux (une `/` reste dans le nom). `null` pour un secret vide.
 */
export function delineaSecretEntry(secret: DelineaSecret): ImportedEntry | null {
  const found: Partial<Record<DelineaRole, string>> = {};
  const labelOf: Partial<Record<DelineaRole, string>> = {};
  const customFields: EntryCustomField[] = [];
  for (const item of secret.items) {
    if (item.value.trim().length === 0) continue;
    // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
    const value = item.slug === 'password' ? item.value : item.value.trim();
    const role = (DELINEA_ROLES as readonly string[]).includes(item.slug) ? (item.slug as DelineaRole) : null;
    if (role !== null && found[role] === undefined) {
      found[role] = value;
      labelOf[role] = item.label;
      continue;
    }
    const field = csvColumnField(item.label, value);
    customFields.push(DELINEA_SECRET_SLUG.test(item.slug) ? { ...field, hidden: true } : field);
  }
  let totp = { secret: '', extraNote: '' };
  const totpKey = found['totp-key'];
  if (totpKey !== undefined) {
    if (/^otpauth:\/\//iu.test(totpKey) || /^[A-Z2-7]+=*$/u.test(totpKey.replace(/[\s-]+/gu, '').toUpperCase())) totp = normalizeTotpSecret(totpKey);
    else customFields.push({ name: labelOf['totp-key']!, value: totpKey, hidden: true });
  }
  const username = found.username ?? '';
  const password = found.password ?? '';
  const uris = found.url !== undefined ? [found.url] : [];
  const notes = joinNotes([found.notes ?? '', totp.extraNote]);
  const name = secret.name.trim();
  const isLogin = username.length > 0 || password.length > 0 || uris.length > 0 || totp.secret.length > 0;
  if (name.length === 0 && !isLogin && notes.length === 0 && customFields.length === 0) return null;
  const folderPath = secret.folder
    .split('\\')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  const entryHost = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
  return {
    type: isLogin ? 'login' : 'note',
    name: name || entryHost || uris[0] || username || 'Sans nom',
    username,
    password,
    uris,
    notes,
    totpSecret: totp.secret,
    favorite: false,
    ...(customFields.length > 0 ? { customFields } : {}),
    ...(folderPath.length > 0 ? { folderPath } : {}),
  };
}

/** Colonnes fixes d'un export CSV Delinea Secret Server ; `Secret Name` n'appartient qu'a lui. */
const DELINEA_COLUMNS = { name: 'secret name', folder: 'folder' } as const;

/**
 * Export CSV de **Delinea Secret Server** (ex-Thycotic) : `Secret Name`, une
 * colonne par champ du modele (`Username`, `URL`, `Password`, `Notes`,
 * `Machine`...), puis `Folder`, `TOTP Key`, `TOTP Backup Codes`. Reconnu a sa
 * colonne `Secret Name`. Sans lui, le lecteur generique rangeait le nom du
 * secret en champ MASQUE (« Secret » est un libelle de secret) et nommait
 * l'entree de son adresse, ou de son identifiant. Lecture : `delineaSecretEntry`.
 * `null` si le CSV n'est pas de cette forme.
 */
function parseDelineaCsv(header: readonly string[], normalized: readonly string[], records: readonly string[]): ImportedEntry[] | null {
  if (!normalized.includes(DELINEA_COLUMNS.name)) return null;
  const entries: ImportedEntry[] = [];
  for (const record of records) {
    const values = parseCsvLine(record);
    const get = (column: string): string => (values[normalized.indexOf(column)] ?? '').trim();
    const items: DelineaSecretItem[] = [];
    header.forEach((label, index) => {
      if (normalized[index] === DELINEA_COLUMNS.name || normalized[index] === DELINEA_COLUMNS.folder) return;
      items.push({ slug: delineaSlug(label), label, value: values[index] ?? '' });
    });
    const entry = delineaSecretEntry({ name: get(DELINEA_COLUMNS.name), folder: get(DELINEA_COLUMNS.folder), items });
    if (entry !== null) entries.push(entry);
  }
  return entries;
}

/** Fiche mSecure : `nom|identifiant` en premiere colonne (l'identifiant interne est ecarte). */
const MSECURE_NAME = /^([\s\S]*)\|\d+$/u;

/** Cellule de champ mSecure : `libelle|code de type|valeur`, la valeur gardant ses `|`. */
const MSECURE_FIELD = /^([^|\n]*)\|(\d+)\|([\s\S]*)$/u;

/** Colonnes fixes d'un export CSV mSecure 6 (sans ligne d'en-tete), puis une cellule par champ. */
const MSECURE_COLUMNS = { name: 0, type: 1, tags: 2, notes: 3, firstField: 4 } as const;

/** Code de type mSecure d'un champ mot de passe : masque quel que soit son libelle. */
const MSECURE_PASSWORD_CODE = '8';

/** Types de fiche mSecure d'un identifiant de site (`Web Logins` : mSecure 5). */
const MSECURE_LOGIN_TYPES = new Set(['login', 'web logins']);

/** Types de fiche mSecure d'une note seule. */
const MSECURE_NOTE_TYPES = new Set(['note', 'secure note', 'secure notes']);

/** Libelles mSecure des roles d'une fiche (comparaison sans casse). */
const MSECURE_ROLES = {
  url: /^(website|url|web site)$/iu,
  username: /^(username|user name|login)$/iu,
  email: /^(e-?mail|e-?mail address)$/iu,
  password: /^password$/iu,
  totp: /^(one[- ]?time password|totp|2fa|otp|authenticator( key)?)$/iu,
  cardNumber: /^card number$/iu,
  cardholder: /^name on card$/iu,
  expiry: /^(expiration|expiration date|expiry date)$/iu,
  cvv: /^(security code|cvv|cvc)$/iu,
  pin: /^pin$/iu,
  bank: /^(bank|bank name|issuing bank)$/iu,
} as const;

type MSecureRole = keyof typeof MSECURE_ROLES;

interface MSecureField {
  readonly label: string;
  readonly code: string;
  /** Valeur brute, jamais rognee : une espace peut faire partie d'un mot de passe. */
  readonly value: string;
}

/**
 * Export CSV de **mSecure 6** (« File > Export > CSV », macOS / Windows /
 * iOS / Android) : aucune ligne d'en-tete ; chaque ligne porte `nom|id`, le
 * type de fiche, les etiquettes, les notes (retours a la ligne ecrits `\n`),
 * puis une cellule `libelle|code|valeur` par champ. Reconnu a son nom suivi
 * d'un identifiant numerique ET a des cellules de champ toutes de cette forme,
 * sur chaque ligne. Sans lui, la ligne passait a Keeper (six colonnes, pas
 * d'en-tete) : le nom devenait un dossier et le mot de passe arrivait comme
 * `Password|8|secret` dans le mauvais champ.
 */
function isMSecureCsv(records: readonly string[]): boolean {
  if (records.length === 0) return false;
  return records.every((record) => {
    const cells = parseCsvLine(record);
    if (cells.length <= MSECURE_COLUMNS.type || !MSECURE_NAME.test(cells[MSECURE_COLUMNS.name]!)) return false;
    if ((cells[MSECURE_COLUMNS.type] ?? '').trim().length === 0) return false;
    return cells.slice(MSECURE_COLUMNS.firstField).every((cell) => cell.length === 0 || MSECURE_FIELD.test(cell));
  });
}

/** Role d'un champ mSecure, d'apres son libelle ; `null` s'il reste un champ personnalise. */
function mSecureRole(label: string): MSecureRole | null {
  for (const role of Object.keys(MSECURE_ROLES) as MSecureRole[]) {
    if (MSECURE_ROLES[role].test(label.trim())) return role;
  }
  return null;
}

/** Champ mSecure garde en champ personnalise : masque si son code ou son libelle designe un secret. */
function mSecureKeptField(field: MSecureField, secret = false): EntryCustomField {
  const hidden = secret || field.code === MSECURE_PASSWORD_CODE;
  const custom = csvColumnField(field.label || 'Champ', hidden ? field.value : field.value.trim());
  return hidden ? { ...custom, hidden: true } : custom;
}

/**
 * Parse un export CSV mSecure 6 ; chaque ligne, la premiere comprise, est une
 * fiche. Identifiant de site (`Login`, `Web Logins`) et toute fiche portant un
 * identifiant ou un mot de passe (e-mail, Wi-Fi, logiciel...) -> identifiant :
 * adresse, identifiant (l'e-mail a defaut), mot de passe jamais rogne, graine
 * TOTP ; `Credit Card` -> carte (titulaire, numero, expiration, cryptogramme,
 * PIN, banque) ; `Note` ou fiche sans secret -> note. Les etiquettes sont
 * reprises (`Unassigned` = aucune) ; tout autre champ devient un champ
 * personnalise, masque si son code (mot de passe) ou son libelle designe un
 * secret.
 */
function parseMSecureCsv(records: readonly string[]): ImportedEntry[] {
  return records.map((record) => {
    const cells = parseCsvLine(record);
    const name = MSECURE_NAME.exec(cells[MSECURE_COLUMNS.name]!)![1]!.trim();
    const kind = (cells[MSECURE_COLUMNS.type] ?? '').trim().toLowerCase();
    const rawTags = (cells[MSECURE_COLUMNS.tags] ?? '').trim();
    const tags = rawTags.toLowerCase() === 'unassigned' ? [] : parseTagList(rawTags);
    const fields: MSecureField[] = cells
      .slice(MSECURE_COLUMNS.firstField)
      .map((cell) => MSECURE_FIELD.exec(cell))
      .filter((match): match is RegExpExecArray => match !== null && match[3]!.trim().length > 0)
      .map((match) => ({ label: match[1]!.trim(), code: match[2]!, value: match[3]! }));
    const notes = [(cells[MSECURE_COLUMNS.notes] ?? '').replace(/\\n/gu, '\n').trim()];
    const customFields: EntryCustomField[] = [];
    const found: Partial<Record<MSecureRole, MSecureField>> = {};
    const uris: string[] = [];
    for (const field of fields) {
      const role = mSecureRole(field.label);
      if (role === 'url') {
        const uri = field.value.trim();
        if (!uris.includes(uri)) uris.push(uri);
      } else if (role !== null && found[role] === undefined) {
        found[role] = field;
      } else {
        customFields.push(mSecureKeptField(field, role === 'password' || role === 'totp' || role === 'cardNumber' || role === 'cvv' || role === 'pin'));
      }
    }
    const isCard = kind === 'credit card' || (found.cardNumber !== undefined && found.username === undefined && found.password === undefined);
    const typed: Record<string, string> = {};
    if (isCard) {
      const cardRoles = { cardNumber: 'number', cardholder: 'cardholder', cvv: 'cvv', pin: 'pin', bank: 'bank' } as const;
      for (const [role, key] of Object.entries(cardRoles) as [keyof typeof cardRoles, string][]) {
        if (found[role] !== undefined) typed[key] = found[role]!.value.trim();
      }
      const expiry = found.expiry?.value.trim() ?? '';
      const readableExpiry = normalizeExpiry(expiry);
      if (readableExpiry !== null) typed['expiry'] = readableExpiry;
      else if (expiry.length > 0) customFields.push(mSecureKeptField(found.expiry!));
    } else {
      // Hors carte, les champs de carte restent des champs : numero et codes masques.
      for (const role of ['cardNumber', 'cardholder', 'expiry', 'cvv', 'pin', 'bank'] as const) {
        const field = found[role];
        if (field !== undefined) customFields.push(mSecureKeptField(field, role === 'cardNumber' || role === 'cvv' || role === 'pin'));
      }
    }
    // Sur une carte, identifiant et mot de passe n'ont pas de place : ils restent des champs.
    let username = '';
    let password = '';
    let totp = { secret: '', extraNote: '' };
    if (isCard) {
      for (const role of ['username', 'email', 'password', 'totp'] as const) {
        const field = found[role];
        if (field !== undefined) customFields.push(mSecureKeptField(field, role === 'password' || role === 'totp'));
      }
    } else {
      username = found.username?.value.trim() ?? '';
      const email = found.email?.value.trim() ?? '';
      if (username.length === 0) username = email;
      else if (email.length > 0 && email !== username) customFields.push(mSecureKeptField(found.email!));
      // Un mot de passe n'est jamais rogne : ses espaces font partie du secret.
      password = found.password?.value ?? '';
      if (found.totp !== undefined) {
        totp = normalizeTotpSecret(found.totp.value);
        if (totp.secret.length === 0) {
          customFields.push(mSecureKeptField(found.totp, true));
          totp = { secret: '', extraNote: '' };
        }
      }
    }
    const noteText = joinNotes([...notes, totp.extraNote]);
    const host = uris.length > 0 ? serverHost(uris[0]!).replace(/^www\./u, '') : '';
    const base = {
      notes: noteText,
      favorite: false,
      ...(customFields.length > 0 ? { customFields } : {}),
      ...(tags.length > 0 ? { tags } : {}),
    };
    if (isCard) {
      const cardFields = normalizeTypedFields('card', typed);
      return {
        type: 'card',
        name: name || cardFields['cardholder'] || 'Sans nom',
        username: '',
        password: '',
        uris: [],
        totpSecret: '',
        ...(Object.keys(cardFields).length > 0 ? { fields: cardFields } : {}),
        ...base,
      };
    }
    const isLogin = MSECURE_LOGIN_TYPES.has(kind) || username.length > 0 || password.length > 0 || uris.length > 0 || totp.secret.length > 0;
    const isNote = MSECURE_NOTE_TYPES.has(kind) ? username.length === 0 && password.length === 0 : !isLogin;
    return {
      type: isNote ? 'note' : 'login',
      name: name || host || uris[0] || username || 'Sans nom',
      username,
      password,
      uris,
      totpSecret: totp.secret,
      ...base,
    };
  });
}

/** Regles par adresse de l'export, limitees aux adresses gardees ; rien si toutes suivent la regle commune. */
function withUriMatch(entry: ImportedEntry): { uriMatch?: Readonly<Record<string, UriMatchRule>> } {
  if (entry.uriMatch === undefined) return {};
  const uriMatch = normalizeUriMatch(entry.uris, new Map(Object.entries(entry.uriMatch)));
  return uriMatch === undefined ? {} : { uriMatch };
}

/** Date importee exploitable : un entier de millisecondes, ni negatif ni dans le futur (horloge de l'export dereglee). */
function importedInstant(at: number | undefined): at is number {
  return at !== undefined && Number.isSafeInteger(at) && at > 0 && at <= Date.now();
}

export function importedEntryToSecrets(entry: ImportedEntry): EntrySecrets {
  const group = typedFieldGroup(entry.type);
  // Une echeance sans mot de passe n'a rien a faire changer ; une date illisible n'affirme rien.
  const passwordExpiry = entry.password.length > 0 && entry.passwordExpiry !== undefined ? normalizeDocumentDate(entry.passwordExpiry) : null;
  // La regle n'existe qu'avec l'echeance qu'elle reporte ; un nombre hors bornes ou non entier n'arme rien.
  const passwordRenewDays =
    passwordExpiry !== null && entry.passwordRenewDays !== undefined && /^\d{1,4}$/.test(entry.passwordRenewDays) ? readPasswordRenewDays({ passwordRenewDays: Number(entry.passwordRenewDays) }) : null;
  // Seules les alertes ignorables sont reprises : une etiquette forgee ne fait jamais taire une fuite.
  const healthIgnored = readIgnoredHealthAlerts({ healthIgnored: entry.healthIgnored });
  const secrets: EntrySecrets = {
    ...(group !== null && entry.fields !== undefined && Object.keys(entry.fields).length > 0 ? { [group]: entry.fields } : {}),
    name: entry.name,
    username: entry.username,
    password: entry.password,
    uris: entry.uris,
    ...withUriMatch(entry),
    notes: entry.notes,
    ...(entry.totpSecret.length > 0 ? { totpSecret: entry.totpSecret } : {}),
    ...(entry.passwordHistory !== undefined && entry.passwordHistory.length > 0 ? { passwordHistory: entry.passwordHistory } : {}),
    ...(entry.password.length > 0 && importedInstant(entry.passwordChangedAt) ? { passwordChangedAt: entry.passwordChangedAt } : {}),
    ...(passwordExpiry !== null ? { passwordExpiry } : {}),
    ...(passwordRenewDays !== null ? { passwordRenewDays } : {}),
    ...(entry.passkeys !== undefined && entry.passkeys.length > 0 ? { passkeys: normalizePasskeys(entry.passkeys) } : {}),
    ...(entry.customFields !== undefined && entry.customFields.length > 0 ? { customFields: normalizeCustomFields(entry.customFields) } : {}),
    ...(entry.reprompt === true ? { reprompt: true } : {}),
    ...(entry.archived === true ? { archived: true } : {}),
    ...(entry.healthExcluded === true ? { healthExcluded: true } : {}),
    ...(healthIgnored.length > 0 ? { healthIgnored: [...healthIgnored] } : {}),
    ...(entry.tags !== undefined && normalizeTags(entry.tags).length > 0 ? { tags: normalizeTags(entry.tags) } : {}),
  };
  return entry.sshKey !== undefined ? withSshKey(secrets, entry.sshKey) : secrets;
}
